<template>
  <el-dialog
    :model-value="props.modelValue"
    append-to-body
    :title="props.dialogTitle"
    width="90%"
    :close-on-click-modal="false"
    @update:model-value="(v) => emit('update:modelValue', v)"
    @close="handleClose"
  >
    <div class="print-preview-container">
      <!-- 工具栏 -->
      <div class="preview-toolbar">
        <el-select
          v-if="scenarioFilterEnabled && showScenarioSelector"
          v-model="selectedScenarioKey"
          placeholder="打印场景"
          style="width: 160px; margin-right: 10px"
          @change="onScenarioFilterChange"
        >
          <el-option
            v-for="opt in scenarioFilterOptions"
            :key="opt.value === '__all__' ? '__all__' : (opt.value || '_empty_')"
            :label="opt.label"
            :value="opt.value"
          />
        </el-select>
        <el-select
          v-if="showTemplateSelector"
          v-model="selectedTemplateId"
          placeholder="选择打印模板"
          style="width: 320px; margin-right: 10px"
          @change="handleTemplateChange"
        >
          <el-option
            v-for="template in selectableTemplates"
            :key="template.template_id"
            :label="templateOptionLabel(template)"
            :value="template.template_id"
          >
            <span>{{ template.template_name }}</span>
            <el-tag
              v-if="template.print_scenario_text || template.print_scenario"
              type="info"
              size="small"
              style="margin-left: 8px"
            >
              {{ template.print_scenario_text || template.print_scenario || '通用' }}
            </el-tag>
            <el-tag v-if="template.is_default" type="success" size="small" style="margin-left: 6px">默认</el-tag>
          </el-option>
        </el-select>
        <el-button
          v-if="showTemplateSelector"
          :loading="templatesLoading"
          title="重新加载模板列表（新建模板后点此刷新）"
          @click="reloadTemplateList"
        >
          <el-icon><Refresh /></el-icon>
          刷新模板
        </el-button>
        <el-select
          v-model="fitModeOverride"
          placeholder="页面适配"
          style="width: 190px; margin-right: 10px"
        >
          <el-option label="模板默认" value="__template__" />
          <el-option label="单页显示" value="one_page" />
          <el-option label="一页宽，多页高" value="multi_page" />
        </el-select>
        <div
          v-if="props.templateType === 'payment_request' && fitModeOverride === 'multi_page'"
          class="toolbar-inline"
        >
          <span class="toolbar-inline__label">每页行数</span>
          <el-input-number
            v-model="rowsPerPageOverride"
            :min="0"
            :max="300"
            :step="1"
            size="small"
            controls-position="right"
            style="width: 130px"
          />
          <span class="toolbar-inline__hint">0=模板默认</span>
          <span class="toolbar-inline__hint">预计 {{ estimatedPages }} 页</span>
        </div>
        <el-button type="primary" plain :loading="previewRefreshing" title="按当前模板重新加载数据并更新预览" @click="handleRefreshPreview">
          <el-icon><View /></el-icon> 预览
        </el-button>
        <el-button @click="handlePrint">
          <el-icon><Printer /></el-icon> 打印
        </el-button>
        <el-button :loading="exportPdfLoading" title="导出 PDF，跨浏览器版式一致，可直接打印" @click="handleExportPdf">
          导出 PDF
        </el-button>
        <el-button
          v-if="props.templateType === 'payment_request'"
          :loading="exportExcelLoading"
          @click="handleExportStatementExcel"
        >
          导出 Excel
        </el-button>
        <el-button @click="handleClose">关闭</el-button>
      </div>
      <div v-if="activeTemplateName" class="preview-template-meta">
        当前模板：{{ activeTemplateName }}（ID: {{ activeTemplateId }}）
      </div>
      
      <!-- 预览内容 -->
      <div class="preview-content" ref="previewContentRef">
        <div
          class="print-page"
          :class="{
            'print-page--label': isCompactLabel,
            'print-page--one-page': isOnePageFit,
            'print-page--compact-tail': compactPageTail,
            'print-page--fixed-footer': useFixedPageFooter,
            'print-page--paged-preview': isMultiPageStatementPreview
          }"
          :style="pageStyle"
          v-if="printData"
        >
          <div class="print-page__content-shell" :style="onePageShellStyle">
            <div
              ref="onePageBlockRef"
              class="print-page__content"
              :style="onePageContentTransformStyle"
            >
          <!-- 页眉（参考线仅在模板「页眉设置」中显示，此处不显示） -->
          <div
            v-if="showPrintHeaderBlock && !isMultiPageStatementPreview"
            class="print-header"
            :style="printHeaderSurfaceStyle"
          >
            <template v-if="shouldRenderHeaderLines(templateConfig)">
              <div
                v-for="(line, li) in templateConfig.headerLines || []"
                :key="'phl-' + li"
                class="print-header-line print-zone-line"
                :style="printHeaderLineRowStyle(line)"
                v-html="renderZoneLineContent(line.content)"
              />
            </template>
            <div
              v-else-if="templateConfig.header"
              v-html="renderHeaderFooterContent(templateConfig.header)"
            />
          </div>
          
          <!-- 主体内容 -->
          <div class="print-body" :style="printBodyStyle">
            <!-- 基本信息字段显示（新格式） -->
            <div
              v-if="templateConfig.basicFields && templateConfig.basicFields.length > 0 && !isMultiPageStatementPreview"
              class="basic-fields-section"
              :style="basicFieldsSectionOuterStyle"
            >
              <template v-for="(row, ri) in basicFieldLayoutRows" :key="'bfr-' + ri">
                <div v-if="row.type === 'split'" class="basic-fields-split-row">
                  <div class="basic-fields-split-left">
                    <div
                      v-for="(field, fi) in row.left"
                      :key="'bf-l-' + ri + '-' + fi"
                      class="basic-fields-cell"
                      :style="getBasicFieldStyle(field)"
                    >
                      <span v-if="field.label" class="basic-fields-label">{{ field.label }}:</span>
                      {{ field.prefix || '' }}{{ getFieldValue(field.field, field) }}{{ field.suffix || '' }}
                    </div>
                  </div>
                  <div class="basic-fields-split-right">
                    <div
                      v-for="(field, fi) in row.right"
                      :key="'bf-r-' + ri + '-' + fi"
                      class="basic-fields-cell basic-fields-cell--rightCol"
                      :style="getBasicFieldStyle(field)"
                    >
                      <span v-if="field.label" class="basic-fields-label">{{ field.label }}:</span>
                      {{ field.prefix || '' }}{{ getFieldValue(field.field, field) }}{{ field.suffix || '' }}
                    </div>
                  </div>
                </div>
                <div v-else-if="row.type === 'grid'" class="basic-fields-grid-row">
                  <div
                    v-for="(field, fi) in row.fields"
                    :key="'bf-g-' + ri + '-' + fi"
                    class="basic-fields-grid-cell"
                    :style="{ ...getBasicFieldStyle(field), width: ((field.__colSpan || 12) / 12 * 100) + '%' }"
                  >
                    <span v-if="field.label" class="basic-fields-label">{{ field.label }}:</span>
                    {{ field.prefix || '' }}{{ getFieldValue(field.field, field) }}{{ field.suffix || '' }}
                  </div>
                </div>
                <template v-else>
                  <div
                    v-for="(field, fi) in row.fields"
                    :key="'bf-f-' + ri + '-' + fi"
                    class="basic-fields-cell basic-fields-cell--full"
                    :style="getBasicFieldStyle(field)"
                  >
                    <span v-if="field.label" class="basic-fields-label">{{ field.label }}:</span>
                    {{ field.prefix || '' }}{{ getFieldValue(field.field, field) }}{{ field.suffix || '' }}
                  </div>
                </template>
              </template>
            </div>
            
            <!-- 旧格式字段显示（兼容） -->
            <template v-else-if="templateConfig.fields && templateConfig.fields.length > 0 && !isMultiPageStatementPreview">
              <div
                v-for="field in templateConfig.fields"
                :key="field.field"
                class="print-field"
                :style="getFieldStyle(field)"
              >
                <span v-if="field.label" style="margin-right: 5px; font-weight: bold;">{{ field.label }}:</span>
                {{ getFieldValue(field.field, field) }}
              </div>
            </template>
            
            <!-- 表格内容（如果是列表数据）；账单可设每页行数拆成多块并强制换页 -->
            <template v-if="shouldRenderTable">
              <div
                class="print-table-region"
                :class="{ 'pure-preview-pages': isMultiPageStatementPreview }"
                :style="printTableRegionStyle"
              >
              <div
                v-for="(tableChunk, chunkIdx) in printTableItemChunks"
                :key="'tbl-chunk-' + chunkIdx"
                :class="isMultiPageStatementPreview ? 'pure-print-page' : 'preview-table-page'"
                :style="isMultiPageStatementPreview ? purePrintPageShellStyle : previewTablePageStyle"
              >
                <div :class="isMultiPageStatementPreview ? 'pure-print-main' : 'preview-table-page__body'"
                  :style="isMultiPageStatementPreview ? purePrintMainAreaStyle : undefined"
                >
                <template v-if="isMultiPageStatementPreview && chunkIdx === 0">
                  <div
                    v-if="showPrintHeaderBlock"
                    class="print-header"
                    :style="printHeaderSurfaceStyle"
                  >
                    <template v-if="shouldRenderHeaderLines(templateConfig)">
                      <div
                        v-for="(line, li) in templateConfig.headerLines || []"
                        :key="'mphl-' + li"
                        class="print-header-line print-zone-line"
                        :style="printHeaderLineRowStyle(line)"
                        v-html="renderZoneLineContent(line.content)"
                      />
                    </template>
                    <div
                      v-else-if="templateConfig.header"
                      v-html="renderHeaderFooterContent(templateConfig.header)"
                    />
                  </div>
                  <div
                    v-if="templateConfig.basicFields && templateConfig.basicFields.length > 0"
                    class="basic-fields-section"
                    :style="basicFieldsSectionOuterStyle"
                  >
                    <template v-for="(row, ri) in basicFieldLayoutRows" :key="'mpbfr-' + ri">
                      <div v-if="row.type === 'split'" class="basic-fields-split-row">
                        <div class="basic-fields-split-left">
                          <div
                            v-for="(field, fi) in row.left"
                            :key="'mpbf-l-' + ri + '-' + fi"
                            class="basic-fields-cell"
                            :style="getBasicFieldStyle(field)"
                          >
                            <span v-if="field.label" class="basic-fields-label">{{ field.label }}:</span>
                            {{ field.prefix || '' }}{{ getFieldValue(field.field, field) }}{{ field.suffix || '' }}
                          </div>
                        </div>
                        <div class="basic-fields-split-right">
                          <div
                            v-for="(field, fi) in row.right"
                            :key="'mpbf-r-' + ri + '-' + fi"
                            class="basic-fields-cell basic-fields-cell--rightCol"
                            :style="getBasicFieldStyle(field)"
                          >
                            <span v-if="field.label" class="basic-fields-label">{{ field.label }}:</span>
                            {{ field.prefix || '' }}{{ getFieldValue(field.field, field) }}{{ field.suffix || '' }}
                          </div>
                        </div>
                      </div>
                      <div v-else-if="row.type === 'grid'" class="basic-fields-grid-row">
                        <div
                          v-for="(field, fi) in row.fields"
                          :key="'mpbf-g-' + ri + '-' + fi"
                          class="basic-fields-grid-cell"
                          :style="{ ...getBasicFieldStyle(field), width: ((field.__colSpan || 12) / 12 * 100) + '%' }"
                        >
                          <span v-if="field.label" class="basic-fields-label">{{ field.label }}:</span>
                          {{ field.prefix || '' }}{{ getFieldValue(field.field, field) }}{{ field.suffix || '' }}
                        </div>
                      </div>
                      <template v-else>
                        <div
                          v-for="(field, fi) in row.fields"
                          :key="'mpbf-f-' + ri + '-' + fi"
                          class="basic-fields-cell basic-fields-cell--full"
                          :style="getBasicFieldStyle(field)"
                        >
                          <span v-if="field.label" class="basic-fields-label">{{ field.label }}:</span>
                          {{ field.prefix || '' }}{{ getFieldValue(field.field, field) }}{{ field.suffix || '' }}
                        </div>
                      </template>
                    </template>
                  </div>
                </template>
                <div
                  class="print-table"
                  :class="{
                    'print-table--chunk-break':
                      shouldBreakAfterTableChunk(chunkIdx) && !isMultiPageStatementPreview
                  }"
                  :style="printTableWrapperStyle"
                >
                  <table :style="getTableStyle()">
                    <colgroup>
                      <col
                        v-for="(pct, ci) in tableColumnWidthPercentsList"
                        :key="'colw-' + ci"
                        :style="{ width: `${pct}%` }"
                      />
                    </colgroup>
                    <thead>
                      <tr :style="getTableRowStyle()">
                        <template v-if="hasConfiguredTableColumns">
                          <th
                            v-for="cell in mergedBodyTableHeaderCells"
                            :key="'th-' + cell.startIndex + '-' + (cell.col.field || '')"
                            :colspan="cell.colspan"
                            :class="{ 'print-table-header--merged': cell.colspan > 1 }"
                            :style="getTableHeaderStyle(cell.col, cell.startIndex, cell.colspan)"
                          >
                            {{ cell.label }}
                          </th>
                        </template>
                        <template v-else-if="enableLegacyTableFallback">
                          <th :style="getLegacyThStyle('center')">序号</th>
                          <th :style="getLegacyThStyle('left')">采购单号</th>
                          <th :style="getLegacyThStyle('left')">品名</th>
                          <th :style="getLegacyThStyle('left')">规格</th>
                          <th :style="getLegacyThStyle('left')">颜色</th>
                          <th :style="getLegacyThStyle('right')">数量</th>
                          <th :style="getLegacyThStyle('left')">单位</th>
                          <th :style="getLegacyThStyle('left')">备注</th>
                        </template>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(item, ridx) in tableChunk"
                        :key="'r-' + chunkIdx + '-' + ridx"
                        :style="getTableRowStyle()"
                      >
                        <template v-if="hasConfiguredTableColumns">
                          <td
                            v-for="(col, cidx) in bodyPrintColumns"
                            :key="'td-' + cidx + '-' + (col.field || '')"
                            :style="getTableCellStyle(col, cidx)"
                          >
                            {{ getTableCellValue(item, col, tableRowGlobalIndex(chunkIdx, ridx)) }}
                          </td>
                        </template>
                        <template v-else-if="enableLegacyTableFallback">
                          <td :style="getLegacyTdStyle('center')">{{ tableRowGlobalIndex(chunkIdx, ridx) + 1 }}</td>
                          <td :style="getLegacyTdStyle('left')">{{ item.order_no || '-' }}</td>
                          <td :style="getLegacyTdStyle('left')">{{ item.product_name || '-' }}</td>
                          <td :style="getLegacyTdStyle('left')">{{ item.specification || '-' }}</td>
                          <td :style="getLegacyTdStyle('left')">
                            <span v-if="item.color_en">{{ item.color_en }}</span>
                            <span v-if="item.color_en && item.color_cn"> / </span>
                            <span v-if="item.color_cn">{{ item.color_cn }}</span>
                            <span v-if="!item.color_en && !item.color_cn">{{ item.color || '-' }}</span>
                          </td>
                          <td :style="getLegacyTdStyle('right')">{{
                            isDeliveryLikePrint(props.templateType)
                              ? formatDeliveryOrderPrintQuantity(item.quantity ?? item.delivery_quantity ?? 0)
                              : formatNumber(item.quantity || item.delivery_quantity || 0)
                          }}</td>
                          <td :style="getLegacyTdStyle('left')">{{ item.unit || '-' }}</td>
                          <td :style="getLegacyTdStyle('left')">{{ item.notes || '-' }}</td>
                        </template>
                      </tr>
                      <!-- 合计行仅在最后一块表展示 -->
                      <tr
                        v-if="
                          chunkIdx === printTableItemChunks.length - 1 &&
                          templateConfig.tableConfig &&
                          templateConfig.tableConfig.columns &&
                          templateConfig.tableConfig.columns.length &&
                          shouldShowTotalsRow(printData, tableConfigForPrintTotals)
                        "
                        :style="getTableRowStyle({ isTotal: true })"
                      >
                        <td
                          v-for="(col, cidx) in bodyPrintColumns"
                          :key="'tot-' + cidx"
                          :style="getTotalRowCellStyle(col, cidx)"
                        >
                          {{ getTotalRowCellText(col, printData, bodyPrintColumns, cidx, templateConfig.tableConfig, props.templateType) }}
                        </td>
                      </tr>
                      <tr
                        v-else-if="
                          chunkIdx === printTableItemChunks.length - 1 &&
                          !(templateConfig.tableConfig && templateConfig.tableConfig.columns && templateConfig.tableConfig.columns.length) &&
                          shouldShowTotalsRow(printData, {
                            columns: PRINT_PREVIEW_LEGACY_COLUMNS,
                            showTotalsRow: templateConfig.tableConfig?.showTotalsRow
                          })
                        "
                        :style="getTableRowStyle({ isTotal: true })"
                      >
                        <td
                          v-for="(col, cidx) in PRINT_PREVIEW_LEGACY_COLUMNS"
                          :key="'tot-' + col.field + '-' + cidx"
                          :style="getTotalRowCellStyleForLegacy(cidx)"
                        >
                          {{ getTotalRowCellText(col, printData, PRINT_PREVIEW_LEGACY_COLUMNS, cidx, templateConfig.tableConfig, props.templateType) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div
                  v-if="isPagedStatementLayout && shouldRenderTailInPagedTable(chunkIdx) && templateConfig.notes && templateConfig.notes.content"
                  class="print-notes"
                  :style="printNotesBlockStyle"
                >
                  {{ templateConfig.notes.content }}
                </div>

                <div
                  v-if="isPagedStatementLayout && shouldRenderTailInPagedTable(chunkIdx) && templateConfig.paymentFooter && templateConfig.paymentFooter.enabled"
                  class="print-payment-footer"
                  :style="printPaymentFooterBlockStyle"
                >
                  <div class="print-payment-footer__title">
                    {{ templateConfig.paymentFooter.noticeText || '请将以上货款汇入以下银行账户' }}
                  </div>
                  <table
                    v-if="paymentFooterBordered"
                    class="print-payment-footer__table"
                    cellspacing="0"
                    cellpadding="0"
                  >
                    <colgroup>
                      <col :style="{ width: paymentFooterLabelColWidth + '%' }" />
                      <col />
                    </colgroup>
                    <tbody>
                      <tr v-for="line in paymentFooterDisplayLines" :key="line.key">
                        <td class="pf-label">{{ line.label }}</td>
                        <td class="pf-value">{{ line.value }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <template v-else>
                    <div
                      v-for="line in paymentFooterDisplayLines"
                      :key="line.key"
                    >
                      {{ line.label }}{{ line.value }}
                    </div>
                  </template>
                </div>

                <div
                  v-if="isPagedStatementLayout && shouldRenderTailInPagedTable(chunkIdx) && showSignatureBlock"
                  :class="statementDualGridActive ? 'print-signatures print-signatures--dual-grid' : 'print-signatures print-signatures--spread'"
                  :style="printSignaturesOuterStyle"
                >
                  <table
                    v-if="statementDualGridActive"
                    class="print-signature-dual-grid"
                    cellspacing="0"
                    cellpadding="0"
                  >
                    <tbody>
                      <tr v-for="(row, ri) in statementDualGridRows" :key="'sig-row-' + ri">
                        <td class="sig-cell sig-cell--left">
                          <span class="sig-lbl">{{ formatDualGridLabel(row.left.label) }}</span>
                          <span class="sig-val">{{ dualGridCellValue(row.left) }}</span>
                        </td>
                        <td class="sig-cell sig-cell--right">
                          <span class="sig-lbl">{{ formatDualGridLabel(row.right.label) }}</span>
                          <span class="sig-val">{{ dualGridCellValue(row.right) }}</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <template v-else>
                    <div
                      v-for="item in signatureDisplayItems"
                      :key="item.id"
                      class="print-signature-slot"
                    >
                      <div class="print-signature-slot__label">{{ displayLabelForItem(item) }}</div>
                      <div class="print-signature-slot__line">
                        {{ signatureFieldValue(item) }}
                      </div>
                    </div>
                  </template>
                </div>

                <div
                  v-if="isPagedStatementLayout && shouldRenderTailInPagedTable(chunkIdx) && shouldRenderClosingLines(templateConfig)"
                  class="print-closing-block"
                  :style="printClosingOuterStyle"
                >
                  <div
                    v-for="(line, cli) in templateConfig.closingLines || []"
                    :key="'pcl-' + chunkIdx + '-' + cli"
                    class="print-closing-line print-zone-line"
                    :style="printClosingLineRowStyle(line)"
                    v-html="renderClosingZoneLineContent(line.content)"
                  />
                </div>

                </div>
                <div
                  v-if="isMultiPageStatementPreview && hasTemplateFooter"
                  class="pure-print-footer"
                  :style="purePrintFooterPinnedStyle"
                  v-html="renderPagedFooterHtml(chunkIdx + 1, printTableItemChunks.length)"
                />
                <div
                  v-else-if="shouldShowPerPagePreviewFooter"
                  class="print-footer print-page-footer-slot"
                  :style="printFooterBarStyle"
                  v-html="renderPagedFooterHtml(chunkIdx + 1, printTableItemChunks.length)"
                />
              </div>
              </div>
            </template>
            
            <!-- 注意事项 -->
            <div 
              v-if="!isPagedStatementLayout && templateConfig.notes && templateConfig.notes.content" 
              class="print-notes"
              :style="printNotesBlockStyle"
            >
              {{ templateConfig.notes.content }}
            </div>

            <div
              v-if="!isPagedStatementLayout && templateConfig.paymentFooter && templateConfig.paymentFooter.enabled"
              class="print-payment-footer"
              :style="printPaymentFooterBlockStyle"
            >
              <div class="print-payment-footer__title">
                {{ templateConfig.paymentFooter.noticeText || '请将以上货款汇入以下银行账户' }}
              </div>
              <table
                v-if="paymentFooterBordered"
                class="print-payment-footer__table"
                cellspacing="0"
                cellpadding="0"
              >
                <colgroup>
                  <col :style="{ width: paymentFooterLabelColWidth + '%' }" />
                  <col />
                </colgroup>
                <tbody>
                  <tr v-for="line in paymentFooterDisplayLines" :key="'pf-np-' + line.key">
                    <td class="pf-label">{{ line.label }}</td>
                    <td class="pf-value">{{ line.value }}</td>
                  </tr>
                </tbody>
              </table>
              <template v-else>
                <div
                  v-for="line in paymentFooterDisplayLines"
                  :key="line.key"
                >
                  {{ line.label }}{{ line.value }}
                </div>
              </template>
            </div>
            
            <!-- 签名栏（items / 兼容旧 enabledFields、fields） -->
            <div
              v-if="!isPagedStatementLayout && showSignatureBlock"
              :class="statementDualGridActive ? 'print-signatures print-signatures--dual-grid' : 'print-signatures print-signatures--spread'"
              :style="printSignaturesOuterStyle"
            >
              <table
                v-if="statementDualGridActive"
                class="print-signature-dual-grid"
                cellspacing="0"
                cellpadding="0"
              >
                <tbody>
                  <tr v-for="(row, ri) in statementDualGridRows" :key="'sig-row-np-' + ri">
                    <td class="sig-cell sig-cell--left">
                      <span class="sig-lbl">{{ formatDualGridLabel(row.left.label) }}</span>
                      <span class="sig-val">{{ dualGridCellValue(row.left) }}</span>
                    </td>
                    <td class="sig-cell sig-cell--right">
                      <span class="sig-lbl">{{ formatDualGridLabel(row.right.label) }}</span>
                      <span class="sig-val">{{ dualGridCellValue(row.right) }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
              <template v-else>
                <div
                  v-for="item in signatureDisplayItems"
                  :key="item.id"
                  class="print-signature-slot"
                >
                  <div class="print-signature-slot__label">{{ displayLabelForItem(item) }}</div>
                  <div class="print-signature-slot__line">
                    {{ signatureFieldValue(item) }}
                  </div>
                </div>
              </template>
            </div>

            <div
              v-if="!isPagedStatementLayout && shouldRenderClosingLines(templateConfig)"
              class="print-closing-block"
              :style="printClosingOuterStyle"
            >
              <div
                v-for="(line, cli) in templateConfig.closingLines || []"
                :key="'cl-' + cli"
                class="print-closing-line print-zone-line"
                :style="printClosingLineRowStyle(line)"
                v-html="renderClosingZoneLineContent(line.content)"
              />
            </div>
          </div>
          
          <!-- 页脚 -->
          <div
            v-if="shouldShowGlobalTemplateFooter"
            class="print-footer"
            :class="{ 'print-footer--repeat-each-page': useRepeatFooterEachPage }"
            :style="printFooterBarStyle"
          >
            <template v-if="shouldRenderFooterLines(templateConfig)">
              <div
                v-for="(line, fi) in templateConfig.footerLines || []"
                :key="'pfl-' + fi"
                class="print-footer-line print-zone-line"
                :style="printFooterLineRowStyle(line)"
                v-html="renderFooterZoneLineContent(line.content)"
              />
            </template>
            <div
              v-else-if="templateConfig.footer"
              v-html="renderFooterLineContent(templateConfig.footer)"
            />
          </div>
            </div>
          </div>
        </div>
        
        <el-empty v-else description="暂无打印数据" />
      </div>
    </div>
  </el-dialog>
</template>

<script setup>
import { ref, computed, watch, nextTick, onUnmounted } from 'vue'
import { ElMessage } from 'element-plus'
import { Printer, View, Refresh } from '@element-plus/icons-vue'
import { getPrintTemplates, getPrintData } from '@/api/printTemplates'
import { labelForScenario } from '@/constants/printScenarios'
import { buildBasicFieldLayoutRows } from '@/utils/printBasicFieldsLayout'
import {
  getTotalRowCellText,
  shouldShowTotalsRow,
  PRINT_PREVIEW_LEGACY_COLUMNS
} from '@/utils/printTableTotals'
import { migrateSignatureItems, displayLabelForItem, resolveSignatureLineValue } from '@/utils/printSignatures'
import {
  getNormalizedTableColumnWidthPercents,
  printTableHeaderCellStyle,
  tableCellTextWrapStyle,
  isTableRowHeightLocked,
  mergeTableConfigFromRaw,
  DEFAULT_PRINT_TABLE_CONFIG,
  splitTableItemRows,
  effectiveTableHeaderTextAlign,
  getTableColumnsForPrint,
  getMergedTableHeaderCells,
  mergedTableHeaderPresentationStyle,
  shouldBlankMoneyCellForPrint,
  resolveTableBorderWidthPx,
  tableBorderLine
} from '@/utils/printTableLayout'
import { formatDeliveryOrderPrintQuantity } from '@/utils/formatDeliveryOrderPrintQuantity'
import { clampHeaderLayoutFromPage } from '@/utils/printHeaderLayoutClamp'
import {
  effectiveBodyFontFamily,
  effectiveHeaderFontFamily,
  resolveBasicFieldFontFamily,
  resolveTableCellFontFamily,
  resolveLegacyTableFontFamily
} from '@/utils/printFontFamily'
import {
  shouldRenderHeaderLines,
  shouldRenderFooterLines,
  shouldRenderClosingLines,
  printZoneLineBoxStyle,
  applyHeaderFooterPagePlaceholders
} from '@/utils/printHeaderFooterLines'
import { stripZoneLineInlineTypography, preserveZoneLineWhitespaceHtml } from '@/utils/printZoneLineContent'
import {
  buildPaymentFooterDisplayLines,
  paymentFooterReservedLineCount
} from '@/utils/paymentFooterBankFields'
import {
  formatDualGridLabel,
  paymentFooterBorderEnabled,
  paymentFooterBlockCssStyle,
  paymentFooterLabelColWidthPercent,
  resolveStatementDualGridCellValue,
  statementDualGridRowPairs,
  useStatementDualSignatureGrid
} from '@/utils/printStatementTailBlocks'
import {
  buildFixedPrintFooterCss,
  buildPageMarginCssString,
  buildPrintPageCounterCss,
  buildRepeatEachPagePrintFooterCss,
  injectPrintFlowPageCounters,
  printPageFooterCssVars,
  resolvePrintFooterLayout,
  useFixedPrintPageFooter,
  useRepeatPrintFooterEachPage
} from '@/utils/printPageFooterLayout'
import { exportPrintPreviewPdf } from '@/utils/exportPrintPreviewPdf'
import {
  buildAtPageSizeCss,
  getInnerPrintableSizePx,
  getPhysicalPaperMm
} from '@/utils/printPageGeometry'
import {
  buildPureDomPrintDocumentHtml,
  clonePurePrintPagesHtml
} from '@/utils/purePagedStatementRender'
import {
  buildPhysicalPrinterEnhancementCss,
  buildPrintPreviewToolbarHtml,
  buildPrintWindowHintHtml,
  cloneStandardPrintContentHtml,
  PRINT_PREVIEW_TOOLBAR_CSS
} from '@/utils/printDomClone'
import {
  resolveStatementLineMaterialName,
  resolveStatementLineClientColorEn,
  resolveStatementLineClientColorCn
} from '@/utils/exportStatementPrintExcel'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  templateType: {
    type: String,
    required: true
  },
  recordId: {
    type: [Number, String],
    default: null
  },
  dialogTitle: {
    type: String,
    default: '打印预览'
  },
  /** 是否显示「场景」筛选；不传则出库单/派工单自动开启 */
  scenarioFilterEnabled: {
    type: Boolean,
    default: undefined
  },
  initialScenarioKey: {
    type: String,
    default: '__all__'
  },
  /** 打开预览时优先选中的模板 ID（如按客户|币别自动匹配） */
  initialTemplateId: {
    type: [Number, String],
    default: null
  },
  /** 为 true 时 initialScenarioKey 仅用于默认选中，不过滤模板列表 */
  preferScenarioForDefault: {
    type: Boolean,
    default: false
  },
  showScenarioSelector: {
    type: Boolean,
    default: true
  },
  showTemplateSelector: {
    type: Boolean,
    default: true
  },
  // 旧模板兼容：当模板未配置表格列时，是否回退显示默认8列表格
  enableLegacyTableFallback: {
    type: Boolean,
    default: true
  },
  /** 包装资料等：自定义拉取打印数据（替代 GET print-data） */
  printDataFetch: {
    type: Function,
    default: null
  },
  /** 派工单分码标签等：只拉取该派工明细行的数据（移动端「本条」） */
  dispatchOrderItemId: {
    type: [Number, String],
    default: null
  },
  /** 可选：只拉取单条分码记录 */
  branchRecordId: {
    type: [Number, String],
    default: null
  }
})

const emit = defineEmits(['update:modelValue', 'printed'])

/** 送货单 / 采购退货单：略放大默认字号 + 宋体族 */
const isDeliveryLikePrint = (type) => type === 'delivery_order' || type === 'purchase_return'
const isDeliveryOrderPrint = computed(() => isDeliveryLikePrint(props.templateType))
const isPhysicalPrinterTemplate = computed(
  () => isDeliveryLikePrint(props.templateType) || props.templateType === 'dispatch_order'
)
const deliveryDotMatrixDefaultFontPx = computed(() => (isPhysicalPrinterTemplate.value ? 16 : 12))

function resolveTablePrintFontSizePx(templateFontSize) {
  const n = Number(templateFontSize)
  if (Number.isFinite(n) && n > 0) return `${n}px`
  return `${deliveryDotMatrixDefaultFontPx.value}px`
}

const printDataQueryExtras = () => {
  const o = {}
  if (props.dispatchOrderItemId != null && props.dispatchOrderItemId !== '') {
    o.dispatch_order_item_id = props.dispatchOrderItemId
  }
  if (props.branchRecordId != null && props.branchRecordId !== '') {
    o.branch_record_id = props.branchRecordId
  }
  return o
}

const templates = ref([])
const templatesLoading = ref(false)
const selectedScenarioKey = ref('__all__')
const selectedTemplateId = ref(null)
const fitModeOverride = ref('__template__')
const rowsPerPageOverride = ref(0)

const showScenarioSelector = computed(() => props.showScenarioSelector)
const showTemplateSelector = computed(() => props.showTemplateSelector)

const scenarioFilterEnabled = computed(() => {
  if (props.scenarioFilterEnabled !== undefined) {
    return props.scenarioFilterEnabled
  }
  return isDeliveryLikePrint(props.templateType) || props.templateType === 'dispatch_order'
})

const scenarioFilterOptions = computed(() => {
  const keys = new Set()
  templates.value.forEach((t) => {
    keys.add((t.print_scenario != null ? String(t.print_scenario) : '').trim())
  })
  const opts = [{ value: '__all__', label: '全部场景' }]
  const sorted = [...keys].sort((a, b) => {
    if (a === '') return -1
    if (b === '') return 1
    return a.localeCompare(b)
  })
  sorted.forEach((k) => {
    opts.push({ value: k, label: labelForScenario(k) })
  })
  return opts
})

const filteredTemplates = computed(() => {
  if (
    props.preferScenarioForDefault ||
    !scenarioFilterEnabled.value ||
    selectedScenarioKey.value === '__all__'
  ) {
    return templates.value
  }
  const key = selectedScenarioKey.value
  return templates.value.filter((t) => (t.print_scenario || '') === key)
})

function templateOptionLabel (template) {
  const scen = (template.print_scenario_text || template.print_scenario || '').trim()
  const base = template.template_name || `模板 ${template.template_id}`
  return scen ? `${base}（${scen}）` : base
}

function pickPreferredTemplate (list) {
  if (!list?.length) return null
  const wantId = props.initialTemplateId
  if (wantId != null && wantId !== '') {
    const hit = list.find((t) => String(t.template_id) === String(wantId))
    if (hit) return hit
  }
  const scen = (props.initialScenarioKey || '').trim()
  if (scen && scen !== '__all__') {
    const matched = list.filter((t) => (t.print_scenario || '') === scen)
    if (matched.length) {
      return matched.find((t) => t.is_default) || matched[0]
    }
  }
  return list.find((t) => t.is_default && t.is_active !== false) || list[0]
}
const enableLegacyTableFallback = computed(() => props.enableLegacyTableFallback)
const hasConfiguredTableColumns = computed(() =>
  !!(templateConfig.value.tableConfig &&
    Array.isArray(templateConfig.value.tableConfig.columns) &&
    templateConfig.value.tableConfig.columns.length > 0)
)

function getActivePrintColumns() {
  const tc = templateConfig.value.tableConfig
  const cols = tc?.columns || []
  if (!Array.isArray(cols) || !cols.length) return PRINT_PREVIEW_LEGACY_COLUMNS
  return getTableColumnsForPrint(cols)
}

const tableConfigForPrintTotals = computed(() => {
  const tc = templateConfig.value.tableConfig || {}
  if (!hasConfiguredTableColumns.value) {
    return { ...tc, columns: PRINT_PREVIEW_LEGACY_COLUMNS }
  }
  return {
    ...tc,
    columns: getTableColumnsForPrint(tc.columns || [])
  }
})

const bodyPrintColumns = computed(() => getActivePrintColumns())
const mergedBodyTableHeaderCells = computed(() => getMergedTableHeaderCells(bodyPrintColumns.value))

/** 与 th/td 列宽一致，打印时由 col 强制定宽，减少各浏览器对 table-layout:fixed 的差异 */
const tableColumnWidthPercentsList = computed(() =>
  getNormalizedTableColumnWidthPercents(getActivePrintColumns())
)

const shouldRenderTable = computed(() =>
  !!(printData.value?.items?.length) && (hasConfiguredTableColumns.value || enableLegacyTableFallback.value)
)

function distributeRowsBackward (chunks, rows, capForChunk) {
  let remain = rows
  for (let i = chunks.length - 1; i >= 0 && remain.length; i--) {
    const cap = capForChunk(i)
    const room = cap - chunks[i].length
    if (room <= 0) continue
    const take = Math.min(room, remain.length)
    chunks[i] = chunks[i].concat(remain.slice(0, take))
    remain = remain.slice(take)
  }
  return remain
}

function relocatePenultimateRowsToLastPage (chunks, per, tailReserve) {
  if (!(tailReserve > 0) || !Array.isArray(chunks) || chunks.length < 2) return
  const penIdx = chunks.length - 2
  if (penIdx < 1) return
  const pen = chunks[penIdx]
  const last = chunks[chunks.length - 1]
  if (!Array.isArray(pen) || !Array.isArray(last)) return
  // 满行倒数第二页：挪 1～2 行到末页（末页有尾部块，通常有空位），给页脚留物理空间
  const maxMove = 2
  let moved = 0
  while (moved < maxMove && pen.length > 0 && pen.length >= per - maxMove) {
    last.unshift(pen.pop())
    moved += 1
  }
}

function splitPagedStatementRows(items, rowsPerPage, tailReserve, headerReserve) {
  const list = Array.isArray(items) ? items : []
  const per = Math.max(1, Math.floor(Number(rowsPerPage)) || 0)
  if (!list.length) return [[]]

  const firstCap = Math.max(1, per - Math.max(0, Math.floor(headerReserve) || 0))
  const tailCap =
    tailReserve > 0 ? Math.max(1, per - Math.max(0, Math.floor(tailReserve) || 0)) : per
  const capForChunk = (idx) => (idx === 0 ? firstCap : per)
  /** 避免拆出 1～3 行的碎页（页脚被挤到下一页、总页数 +1） */
  const minOrphanRows = Math.max(4, Math.floor(per * 0.2))

  const chunks = []
  let remain = list.slice()

  if (remain.length > firstCap) {
    chunks.push(remain.slice(0, firstCap))
    remain = remain.slice(firstCap)
  }

  while (remain.length > per) {
    chunks.push(remain.slice(0, per))
    remain = remain.slice(per)
  }

  if (remain.length) {
    chunks.push(remain)
  } else if (!chunks.length) {
    chunks.push(list)
  }

  if (tailReserve > 0 && chunks.length > 1) {
    let last = chunks.pop()

    while (last.length > tailCap) {
      const excess = last.length - tailCap
      let peel = last.slice(0, excess)
      last = last.slice(excess)

      peel = distributeRowsBackward(chunks, peel, capForChunk)
      if (!peel.length) continue

      const take = Math.min(per, peel.length)
      if (take < minOrphanRows) {
        last = peel.concat(last)
        break
      }

      chunks.push(peel.slice(0, take))
      const rest = peel.slice(take)
      if (rest.length) {
        last = rest.concat(last)
      }
    }

    chunks.push(last)
  }

  relocatePenultimateRowsToLastPage(chunks, per, tailReserve)

  return chunks.length ? chunks : [list]
}

const isPagedStatementLayout = computed(() =>
  props.templateType === 'payment_request' && effectiveFitMode.value === 'multi_page'
)

const effectiveRowsPerPage = computed(() => {
  let n = Math.floor(Number(rowsPerPageOverride.value))
  if (!(n > 0)) {
    const tc = templateConfig.value.tableConfig
    n = Math.floor(Number(tc?.rowsPerPage)) || 0
  }
  return n
})

const reservedRowsForTail = computed(() => {
  if (!isPagedStatementLayout.value) return 0
  let reserve = 0
  if (shouldRenderFooterLines(templateConfig.value)) {
    reserve += Math.min(2, (templateConfig.value.footerLines || []).length)
  } else if (templateConfig.value.footer) {
    reserve += 1
  }
  if (templateConfig.value.notes?.content) reserve += 1
  if (templateConfig.value.paymentFooter?.enabled) {
    reserve += Math.min(3, paymentFooterReservedLineCount(templateConfig.value.paymentFooter))
  }
  if (showSignatureBlock.value) reserve += statementDualGridActive.value ? 3 : 2
  return Math.min(5, Math.max(0, reserve))
})

const printTableItemChunks = computed(() => {
  const items = printData.value?.items
  const fallback = Array.isArray(items) ? items : []
  let chunks
  if (isPagedStatementLayout.value) {
    chunks = splitPagedStatementRows(
      items,
      effectiveRowsPerPage.value,
      reservedRowsForTail.value,
      firstPageHeaderRowReserve.value
    )
  } else {
    chunks = splitTableItemRows(props.templateType, effectiveRowsPerPage.value, items)
  }
  const filtered = (chunks || []).filter((x) => Array.isArray(x) && x.length > 0)
  if (filtered.length) return filtered
  if (fallback.length) return [fallback]
  return []
})

const estimatedPages = computed(() => {
  const chunks = printTableItemChunks.value || []
  return chunks.length || 1
})
const isMultiPageStatementPreview = computed(() =>
  isPagedStatementLayout.value &&
  (printTableItemChunks.value?.length || 0) > 1
)
const shouldUsePurePreviewPaged = computed(() =>
  isPagedStatementLayout.value && (printTableItemChunks.value?.length || 0) > 1
)

const hasTemplateFooter = computed(
  () =>
    shouldRenderFooterLines(templateConfig.value) ||
    !!(templateConfig.value.footer && String(templateConfig.value.footer).trim())
)

const useRepeatFooterEachPage = computed(() =>
  useRepeatPrintFooterEachPage(templateConfig.value.page, {
    disabled: shouldUsePurePreviewPaged.value,
    hasFooter: hasTemplateFooter.value
  })
)

/** 按行数切分多页时，每块预览页底部各放一条页脚 */
const shouldShowPerPagePreviewFooter = computed(
  () =>
    useRepeatFooterEachPage.value &&
    (printTableItemChunks.value?.length || 0) > 1 &&
    !shouldUsePurePreviewPaged.value
)

const shouldShowGlobalTemplateFooter = computed(
  () => hasTemplateFooter.value && !shouldUsePurePreviewPaged.value && !shouldShowPerPagePreviewFooter.value
)

const showPrintHeaderBlock = computed(
  () =>
    shouldRenderHeaderLines(templateConfig.value) ||
    !!(templateConfig.value.header && String(templateConfig.value.header).trim())
)

/** 首页含页眉/基本信息，表格行数相应减少 */
const firstPageHeaderRowReserve = computed(() => {
  if (!isPagedStatementLayout.value) return 0
  let reserve = 0
  if (showPrintHeaderBlock.value) {
    reserve += shouldRenderHeaderLines(templateConfig.value)
      ? Math.min(3, (templateConfig.value.headerLines || []).length)
      : 1
  }
  if (templateConfig.value.basicFields?.length) {
    reserve += Math.min(5, basicFieldLayoutRows.value.length)
  }
  return Math.min(6, reserve)
})

const printHeaderLineRowStyle = (line) => ({
  ...printZoneLineBoxStyle(line, templateConfig.value.page || defaultPrintPage(), 'header'),
  wordBreak: 'break-word'
})

const printFooterLineRowStyle = (line) => ({
  ...printZoneLineBoxStyle(line, templateConfig.value.page || defaultPrintPage(), 'footer'),
  width: '100%',
  whiteSpace: isMultiPageStatementPreview.value ? 'normal' : 'nowrap',
  wordBreak: isMultiPageStatementPreview.value ? 'break-word' : 'keep-all'
})

const printClosingLineRowStyle = (line) => ({
  ...printZoneLineBoxStyle(line, templateConfig.value.page || defaultPrintPage(), 'closing'),
  width: '100%',
  wordBreak: 'break-word'
})

const printClosingOuterStyle = computed(() => {
  const p = templateConfig.value.page || defaultPrintPage()
  const mt = Math.max(0, Number(p.closingMarginTopPx ?? 6) || 0)
  return {
    marginTop: `${mt}px`,
    lineHeight: String(clampPrintBlockLineHeight(p.closingContentLineHeight ?? p.signaturesLineHeight, 1.22)),
    fontFamily: effectiveBodyFontFamily(p, props.templateType)
  }
})
const previewTablePageStyle = computed(() => {
  if (!shouldShowPerPagePreviewFooter.value) return {}
  const p = templateConfig.value.page || defaultPrintPage()
  const ph = getInnerPrintableSizePx(p)
  return {
    border: '1px dashed #d1d5db',
    borderRadius: '4px',
    padding: '8px 10px',
    marginBottom: '12px',
    background: '#fff',
    minHeight: `${Math.max(480, Math.round(ph))}px`,
    display: 'flex',
    flexDirection: 'column'
  }
})

/** 账单多页预览：每页独立纸张 + 模板页边距（虚线框=整页） */
const pageMarginBoxCss = (page) => {
  const p = page || defaultPrintPage()
  return {
    top: `${p.marginTop ?? 10}mm`,
    right: `${p.marginRight ?? 10}mm`,
    bottom: `${p.marginBottom ?? 10}mm`,
    left: `${p.marginLeft ?? 10}mm`
  }
}

const purePrintPageShellStyle = computed(() => {
  const config = templateConfig.value.page || defaultPrintPage()
  const isCustom = String(config.paperSize || '').toLowerCase() === 'custom'
  const width = isCustom
    ? `${Number(config.customWidthMm || 50)}mm`
    : (config.paperSize === 'A4' ? '210mm' : config.paperSize === 'A3' ? '297mm' : '216mm')
  const height = isCustom
    ? `${Number(config.customHeightMm || 30)}mm`
    : (config.paperSize === 'A4' ? '297mm' : config.paperSize === 'A3' ? '420mm' : '279mm')
  const w = config.orientation === 'landscape' ? height : width
  const h = config.orientation === 'landscape' ? width : height
  return {
    width: w,
    height: h,
    minHeight: h,
    maxHeight: h,
    margin: '0 auto',
    padding: 0,
    position: 'relative',
    backgroundColor: '#fff',
    boxShadow: '0 0 10px rgba(0,0,0,0.1)',
    boxSizing: 'border-box',
    overflow: 'hidden',
    fontFamily: effectiveBodyFontFamily(config, props.templateType),
    '--print-table-border': tableBorderCss.value,
    '--print-table-border-width': `${tableBorderWidthPx.value}px`,
    '--print-footer-height': printFooterLayout.value.heightCss
  }
})

/** 正文区：按模板页边距 + 页脚高度绝对定位，每页一致 */
const purePrintMainAreaStyle = computed(() => {
  const config = templateConfig.value.page || defaultPrintPage()
  const m = pageMarginBoxCss(config)
  const { heightCss } = printFooterLayout.value
  return {
    position: 'absolute',
    top: m.top,
    left: m.left,
    right: m.right,
    bottom: `calc(${m.bottom} + ${heightCss})`,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    minHeight: 0,
    fontFamily: effectiveBodyFontFamily(config, props.templateType)
  }
})

/** 页脚：贴每页底边页边距处，不受表格行数影响 */
const purePrintFooterPinnedStyle = computed(() => {
  const p = templateConfig.value.page || defaultPrintPage()
  const m = pageMarginBoxCss(p)
  const { heightCss } = printFooterLayout.value
  const align = normalizeFooterAlign(templateConfig.value.footerAlign)
  return {
    position: 'absolute',
    left: m.left,
    right: m.right,
    bottom: m.bottom,
    height: heightCss,
    minHeight: heightCss,
    overflow: 'visible',
    boxSizing: 'border-box',
    padding: '0 10px',
    borderTop: '1px solid #eee',
    textAlign: align,
    lineHeight: '1.2',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    fontFamily: effectiveBodyFontFamily(p, props.templateType),
    background: '#fff'
  }
})

const tableRowGlobalIndex = (chunkIdx, rowIdx) => {
  if (props.templateType !== 'payment_request') {
    return rowIdx
  }
  const chunks = printTableItemChunks.value || []
  let offset = 0
  for (let i = 0; i < chunkIdx; i++) {
    offset += chunks[i]?.length || 0
  }
  return offset + rowIdx
}

const shouldBreakAfterTableChunk = (chunkIdx) => {
  const ch = printTableItemChunks.value
  return ch.length > 1 && chunkIdx < ch.length - 1
}

// 打印只使用启用模板，避免自动命中停用/旧版导致桌面与移动端不一致
const selectableTemplates = computed(() =>
  filteredTemplates.value.filter((t) => t.is_active !== false)
)

const defaultPrintPage = () => ({
  paperSize: 'A4',
  orientation: 'portrait',
  marginTop: 10,
  marginRight: 10,
  marginBottom: 10,
  marginLeft: 10,
  customWidthMm: 50,
  customHeightMm: 30,
  /** 多页自然流式换行 | 等比缩至可打印区高度（与 Excel「整页为单页」类似） */
  fitMode: 'multi_page',
  /** 为 true 时略压缩表格/收款/签字区间距，避免略超页尾 */
  compactPageTail: false,
  /** 页眉与上方内容/正文之间的留白（px） */
  headerMarginTopPx: 0,
  headerMarginBottomPx: 4,
  /** 页眉富文本行高（倍数，如 1.25） */
  headerContentLineHeight: 1.22,
  /** 页眉区域内边距（px） */
  headerPaddingPx: 4,
  /** 台头基准字号（px），富文本未单独设字号时整体变矮/变高 */
  headerRootFontSizePx: 11,
  /** 台头段落上下间距（em） */
  headerParagraphGapEm: 0.08,
  /** 台头 h1/h2/h3 上下间距（em） */
  headerRichHeadingMarginEm: 0.12,
  /** 台头内图片最大高度（px），避免 Logo 撑高整页 */
  headerInlineImageMaxHeightPx: 48,
  /** 台头整体缩放（约 0.65–1），仅影响页眉块；部分浏览器打印用 zoom */
  headerBlockZoom: 1,
  /** 页眉下方「基本信息」块上下外边距（px） */
  basicFieldsSectionMarginYpx: 2,
  /** 基本信息各行/栅格行间距（px），对应区块 CSS 变量 --bf-row-gap */
  basicFieldsRowGapPx: 2,
  /** 基本信息正文行高（倍数） */
  basicFieldsLineHeight: 1.22,
  /** 签名栏标签与签字行区域行高（倍数） */
  signaturesLineHeight: 1.22,
  /** 基本信息与表格之间的间距（px） */
  tableSectionMarginTopPx: 0,
  /** 表格与「注意事项」之间的间距（px） */
  notesMarginTopPx: 6,
  /** 表格/注意事项与签名栏之间的间距（px） */
  signaturesMarginTopPx: 10,
  /** 收款信息块上边距（px） */
  paymentFooterMarginTopPx: 8,
  /** 正文默认 font-family（CSS 栈）；空则按模板类型自动 */
  defaultFontFamily: '',
  /** 页眉区域默认 font-family；空则与正文一致 */
  headerFontFamily: '',
  /** 分行页眉：每行最小高度（px），0 则仅按行高倍数与内容 */
  headerLineMinHeightPx: 0,
  /** 分行页脚：每行最小高度（px） */
  footerLineMinHeightPx: 0,
  /** 分行页眉行间底横线（线格本效果） */
  headerLineBorderBottom: false,
  footerLineBorderBottom: false,
  /** 分行页脚正文行高（倍数） */
  footerContentLineHeight: 1.22,
  /** 页脚贴页面底固定，不随正文浮动 */
  footerFixedToPageBottom: true,
  /** 打印时每页重复页脚（含浏览器自动分页） */
  footerRepeatEachPage: true,
  /** 页脚固定区高度（mm） */
  footerReservedHeightMm: 7
})

const defaultTableConfig = () => ({ ...DEFAULT_PRINT_TABLE_CONFIG })

const printData = ref(null)
const templateConfig = ref({
  page: defaultPrintPage(),
  fields: [],
  header: '',
  footer: '',
  footerAlign: 'left',
  tableConfig: defaultTableConfig()
})
const activeTemplateId = ref(null)
const activeTemplateName = ref('')
const previewRefreshing = ref(false)
const exportExcelLoading = ref(false)
const exportPdfLoading = ref(false)
const previewContentRef = ref(null)
/** 单页适配时测量/观察的内容块（与模板 ref="onePageBlockRef" 对应） */
const onePageBlockRef = ref(null)

const basicFieldLayoutRows = computed(() =>
  buildBasicFieldLayoutRows(templateConfig.value.basicFields || [])
)

const signatureDisplayItems = computed(() =>
  migrateSignatureItems(templateConfig.value.signatures)
)

const statementDualGridActive = computed(() =>
  useStatementDualSignatureGrid(templateConfig.value.signatures, props.templateType)
)

const statementDualGridRows = computed(() =>
  statementDualGridRowPairs(templateConfig.value.signatures?.statementDualGrid)
)

const paymentFooterBordered = computed(() =>
  paymentFooterBorderEnabled(templateConfig.value.paymentFooter)
)

const paymentFooterLabelColWidth = computed(() =>
  paymentFooterLabelColWidthPercent(templateConfig.value.paymentFooter)
)

const showSignatureBlock = computed(() =>
  statementDualGridActive.value || signatureDisplayItems.value.length > 0
)

// 页面样式
const pageStyle = computed(() => {
  const config = templateConfig.value.page || defaultPrintPage()
  const isCustom = String(config.paperSize || '').toLowerCase() === 'custom'
  const width = isCustom
    ? `${Number(config.customWidthMm || 50)}mm`
    : (config.paperSize === 'A4' ? '210mm' : config.paperSize === 'A3' ? '297mm' : '216mm')
  const height = isCustom
    ? `${Number(config.customHeightMm || 30)}mm`
    : (config.paperSize === 'A4' ? '297mm' : config.paperSize === 'A3' ? '420mm' : '279mm')
  
  // 使用配置的页边距
  const marginTop = `${config.marginTop ?? 10}mm`
  const marginRight = `${config.marginRight ?? 10}mm`
  const marginBottom = `${config.marginBottom ?? 10}mm`
  const marginLeft = `${config.marginLeft ?? 10}mm`
  
  const w = config.orientation === 'landscape' ? height : width
  const h = config.orientation === 'landscape' ? width : height
  if (isMultiPageStatementPreview.value) {
    return {
      width: w,
      minHeight: 'auto',
      height: 'auto',
      margin: '0 auto',
      padding: 0,
      backgroundColor: 'transparent',
      boxShadow: 'none',
      boxSizing: 'border-box',
      fontFamily: effectiveBodyFontFamily(config, props.templateType),
      ...printPageFooterCssVars(config)
    }
  }
  return {
    width: w,
    minHeight: h,
    height: 'auto',
    margin: '0 auto',
    padding: `${marginTop} ${marginRight} ${marginBottom} ${marginLeft}`,
    backgroundColor: '#fff',
    boxShadow: '0 0 10px rgba(0,0,0,0.1)',
    boxSizing: 'border-box',
    fontFamily: effectiveBodyFontFamily(config, props.templateType),
    ...printPageFooterCssVars(config)
  }
})

const useFixedPageFooter = computed(() =>
  useFixedPrintPageFooter(templateConfig.value.page, {
    disabled: shouldUsePurePreviewPaged.value && !useRepeatFooterEachPage.value
  })
)

const printFooterLayout = computed(() => {
  const page = templateConfig.value.page || defaultPrintPage()
  const base = resolvePrintFooterLayout(page)
  if (!shouldUsePurePreviewPaged.value) return base

  let heightMm = base.heightMm
  if (shouldRenderFooterLines(templateConfig.value)) {
    const lineCount = (templateConfig.value.footerLines || []).filter((line) => {
      const t = String(line?.content ?? '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
      return t.length > 0
    }).length
    heightMm = Math.max(heightMm, Math.min(22, 2 + Math.max(1, lineCount) * 4))
  } else if (templateConfig.value.footer && String(templateConfig.value.footer).trim()) {
    heightMm = Math.max(heightMm, 6)
  }
  return { ...base, heightMm, heightCss: `${heightMm}mm` }
})

const isCompactLabel = computed(() => {
  const p = templateConfig.value.page || {}
  if (String(p.paperSize || '').toLowerCase() !== 'custom') return false
  const w = Number(p.customWidthMm || 0)
  const h = Number(p.customHeightMm || 0)
  if (!Number.isFinite(w) || !Number.isFinite(h)) return false
  const maxSide = Math.max(w, h)
  return maxSide <= 80
})

const effectiveFitMode = computed(() => {
  if (fitModeOverride.value && fitModeOverride.value !== '__template__') {
    return fitModeOverride.value
  }
  return (templateConfig.value.page || {}).fitMode || 'multi_page'
})
const isOnePageFit = computed(() => effectiveFitMode.value === 'one_page')
const compactPageTail = computed(() => (templateConfig.value.page || {}).compactPageTail === true)

/** 页眉外边距、行高、内边距、台头压缩（与模板页眉设置一致） */
const printHeaderSurfaceStyle = computed(() => {
  const p = templateConfig.value.page || {}
  const top = Math.max(0, Number(p.headerMarginTopPx ?? 0) || 0)
  const bottom = Math.max(0, Number(p.headerMarginBottomPx ?? 0) || 0)
  const pad = Math.max(0, Number(p.headerPaddingPx ?? 4) || 0)
  const { lhCss, fs, gapEm, hmEm, imgPx, zoom } = clampHeaderLayoutFromPage(p)
  const o = {
    marginTop: `${top}px`,
    marginBottom: `${bottom}px`,
    lineHeight: lhCss,
    padding: `${pad}px 10px`,
    fontSize: `${fs}px`,
    fontFamily: effectiveHeaderFontFamily(p, props.templateType),
    '--header-p-gap': `${gapEm}em`,
    '--header-h-margin': `${hmEm}em`,
    '--header-img-max': `${imgPx}px`
  }
  if (zoom < 0.999) {
    o.zoom = String(zoom)
  }
  return o
})

function clampPrintBlockLineHeight(raw, fallback = 1.22) {
  const n = Number(raw)
  if (!Number.isFinite(n) || n <= 0) return fallback
  return Math.min(2.2, Math.max(1, n))
}

const basicFieldsSectionOuterStyle = computed(() => {
  const p = templateConfig.value.page || {}
  const y = Math.max(0, Number(p.basicFieldsSectionMarginYpx ?? 2) || 0)
  const rowGap = Math.max(0, Number(p.basicFieldsRowGapPx ?? 2) || 0)
  const lh = clampPrintBlockLineHeight(p.basicFieldsLineHeight, 1.22)
  return {
    margin: `${y}px 0`,
    '--bf-row-gap': `${rowGap}px`,
    lineHeight: String(lh),
    fontFamily: effectiveBodyFontFamily(p, props.templateType)
  }
})

/** 签名栏行高（与模板设置一致） */
const printSignaturesOuterStyle = computed(() => {
  const p = templateConfig.value.page || {}
  const lh = clampPrintBlockLineHeight(p.signaturesLineHeight, 1.22)
  const mt = Math.max(0, Number(p.signaturesMarginTopPx ?? 10) || 0)
  return {
    marginTop: `${mt}px`,
    gap: '8px',
    lineHeight: String(lh),
    fontFamily: effectiveBodyFontFamily(p, props.templateType)
  }
})

/** 基本信息区域与表格之间的间距 */
const printTableRegionStyle = computed(() => {
  const t = Math.max(
    0,
    Number((templateConfig.value.page || {}).tableSectionMarginTopPx ?? 0) || 0
  )
  return { marginTop: `${t}px` }
})
const onePageScale = ref(1)
const onePageShellH = ref(0)

const onePageShellStyle = computed(() => {
  if (!isOnePageFit.value) return {}
  const h = onePageShellH.value
  if (!h) return { overflow: 'hidden', width: '100%' }
  return { overflow: 'hidden', width: '100%', height: `${h}px` }
})

const onePageContentTransformStyle = computed(() => {
  if (!isOnePageFit.value) return {}
  const s = onePageScale.value
  if (!s || s >= 0.9999) return {}
  return {
    transform: `scale(${s})`,
    transformOrigin: 'top left',
    width: `${100 / s}%`,
    willChange: 'transform'
  }
})

const tableBorderWidthPx = computed(() =>
  resolveTableBorderWidthPx(templateConfig.value.tableConfig)
)

const tableBorderCss = computed(() =>
  tableBorderLine(templateConfig.value.tableConfig)
)

const printTableWrapperStyle = computed(() => {
  const bw = tableBorderWidthPx.value
  if (isMultiPageStatementPreview.value) {
    return {
      marginTop: '0',
      marginBottom: '0',
      flex: '1 1 auto',
      minHeight: 0,
      overflow: 'hidden',
      paddingBottom: `${bw}px`,
      boxSizing: 'border-box'
    }
  }
  const compact = compactPageTail.value
  /** 与表格区的间距已由 print-table-region 的 tableSectionMarginTopPx 承担，此处不再叠加大块上边距 */
  return { marginTop: '0', marginBottom: compact ? '4px' : '8px' }
})

const printNotesBlockStyle = computed(() => {
  const p = templateConfig.value.page || {}
  const base = Math.max(0, Number(p.notesMarginTopPx ?? 6) || 0)
  const mt = compactPageTail.value ? Math.max(0, base - 2) : base
  return {
    marginTop: `${mt}px`,
    fontSize: isDeliveryOrderPrint.value ? '14px' : '11px',
    lineHeight: 1.45,
    whiteSpace: 'pre-wrap',
    fontFamily: effectiveBodyFontFamily(p, props.templateType)
  }
})

const printPaymentFooterBlockStyle = computed(() => {
  const p = templateConfig.value.page || {}
  const pf = templateConfig.value.paymentFooter || {}
  const mt = Math.max(0, Number(p.paymentFooterMarginTopPx ?? 8) || 0)
  return {
    ...paymentFooterBlockCssStyle(pf, p, props.templateType, mt),
    borderTop: '1px dashed #dcdfe6',
    paddingTop: '6px'
  }
})

const printBodyStyle = computed(() => {
  const p = templateConfig.value.page || defaultPrintPage()
  const ff = effectiveBodyFontFamily(p, props.templateType)
  if (useFixedPageFooter.value) {
    return { minHeight: '0', flex: '1 1 auto', fontFamily: ff }
  }
  if (!isPagedStatementLayout.value && !isCompactLabel.value) {
    return { minHeight: '0', flex: '1 1 auto', fontFamily: ff }
  }
  if (isCompactLabel.value) {
    return { minHeight: '0', flex: '0 0 auto', fontFamily: ff }
  }
  return { minHeight: '0', flex: '0 0 auto', fontFamily: ff }
})

function recomputeOnePageFit() {
  const p = templateConfig.value.page || defaultPrintPage()
  if (effectiveFitMode.value !== 'one_page') {
    onePageScale.value = 1
    onePageShellH.value = 0
    return
  }
  if (!onePageBlockRef.value) return
  const el = onePageBlockRef.value
  const { w: tw, h: th } = getInnerPrintableSizePx(p)
  const h = el.scrollHeight
  const w = Math.max(el.scrollWidth, el.getBoundingClientRect().width) || 1
  if (!h) return
  const s = Math.min(1, th / h, tw / w)
  onePageScale.value = s
  onePageShellH.value = h * s
}

let onePageResizeObserver = null
watch(
  [() => props.modelValue, printData, isOnePageFit, fitModeOverride],
  async () => {
    await nextTick()
    recomputeOnePageFit()
  },
  { deep: true, flush: 'post' }
)
watch(
  () => templateConfig.value?.page,
  async () => {
    await nextTick()
    recomputeOnePageFit()
  },
  { deep: true, flush: 'post' }
)
watch(
  fitModeOverride,
  async () => {
    await nextTick()
    recomputeOnePageFit()
  },
  { flush: 'post' }
)
watch(
  [onePageBlockRef, isOnePageFit, () => props.modelValue],
  () => {
    if (onePageResizeObserver) {
      onePageResizeObserver.disconnect()
      onePageResizeObserver = null
    }
    if (!isOnePageFit.value || !props.modelValue || !onePageBlockRef.value) return
    onePageResizeObserver = new ResizeObserver(() => {
      recomputeOnePageFit()
    })
    onePageResizeObserver.observe(onePageBlockRef.value)
  },
  { flush: 'post' }
)
onUnmounted(() => {
  if (onePageResizeObserver) {
    onePageResizeObserver.disconnect()
    onePageResizeObserver = null
  }
})

const normalizeFooterAlign = (align) => {
  return ['left', 'center', 'right'].includes(align) ? align : 'left'
}

const printFooterBarStyle = computed(() => {
  const p = templateConfig.value.page || defaultPrintPage()
  const align = normalizeFooterAlign(templateConfig.value.footerAlign)
  const base = {
    textAlign: align,
    fontFamily: effectiveBodyFontFamily(p, props.templateType)
  }
  const { heightCss } = printFooterLayout.value
  if (isMultiPageStatementPreview.value) {
    return purePrintFooterPinnedStyle.value
  }
  if (!useFixedPageFooter.value) return base
  return {
    ...base,
    flex: `0 0 ${heightCss}`,
    height: heightCss,
    minHeight: heightCss,
    maxHeight: heightCss,
    marginTop: 'auto',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    boxSizing: 'border-box',
    padding: '0 10px'
  }
})

const pickAndLoadTemplate = async () => {
  const list = selectableTemplates.value
  if (!list.length) {
    selectedTemplateId.value = null
    printData.value = null
    activeTemplateId.value = null
    activeTemplateName.value = ''
    if (!templates.value.length) {
      ElMessage.warning('当前没有可用的打印模板，请先在「打印模板管理」中创建并启用')
    } else {
      ElMessage.warning('当前场景下没有模板，可切换到「全部场景」或到模板管理为该场景新增模板')
    }
    return
  }
  const preferred = pickPreferredTemplate(list)
  selectedTemplateId.value = preferred.template_id
  await handleTemplateChange(showTemplateSelector.value ? preferred.template_id : null)
}

const onScenarioFilterChange = async () => {
  await nextTick()
  await pickAndLoadTemplate()
}

// 加载模板列表
const loadTemplates = async () => {
  if ((props.recordId == null || props.recordId === '') && !props.printDataFetch) {
    return
  }
  templatesLoading.value = true
  try {
    const res = await getPrintTemplates({ template_type: props.templateType })
    if (res.success) {
      templates.value = res.data || []
      if (scenarioFilterEnabled.value && !props.preferScenarioForDefault) {
        selectedScenarioKey.value = props.initialScenarioKey || '__all__'
      } else {
        selectedScenarioKey.value = '__all__'
      }
      await nextTick()
      await pickAndLoadTemplate()
    }
  } catch (error) {
    console.error('加载模板列表失败:', error)
    ElMessage.error('加载打印模板列表失败')
  } finally {
    templatesLoading.value = false
  }
}

const reloadTemplateList = async () => {
  await loadTemplates()
  if (templates.value.length) {
    ElMessage.success('模板列表已刷新')
  }
}

// 模板改变
const handleTemplateChange = async (templateId) => {
  if ((props.recordId == null || props.recordId === '') && !props.printDataFetch) {
    return
  }
  try {
    const req = {
      ...printDataQueryExtras()
    }
    // 隐藏模板选择（移动端）时，不传 template_id，交由后端按场景默认模板解析，确保与桌面端锁定版本一致
    if (showTemplateSelector.value && templateId) {
      req.template_id = templateId
    }
    if (scenarioFilterEnabled.value && selectedScenarioKey.value !== '__all__') {
      req.print_scenario = selectedScenarioKey.value
    }
    let res
    if (props.printDataFetch) {
      res = await props.printDataFetch(req)
    } else {
      res = await getPrintData(props.templateType, props.recordId, { ...req })
    }
    if (res.success) {
      const tpl = res.data?.template
      if (!tpl) {
        ElMessage.error(res.message || '打印模板数据不完整')
        return
      }
      activeTemplateId.value = tpl.template_id || null
      activeTemplateName.value = tpl.template_name || ''
      selectedTemplateId.value = tpl.template_id || selectedTemplateId.value
      const raw = tpl.template_config || {}
      const rawTc = raw.tableConfig || {}
      templateConfig.value = {
        fields: [],
        header: '',
        footer: '',
        footerAlign: 'left',
        ...raw,
        page: { ...defaultPrintPage(), ...(raw.page || {}) },
        tableConfig: mergeTableConfigFromRaw(rawTc)
      }
      printData.value = res.data.print_data
      
      // 页眉/页脚变量在渲染时替换，避免覆盖原模板内容
    } else {
      ElMessage.error(res.message || '获取打印数据失败')
    }
  } catch (error) {
    ElMessage.error('获取打印数据失败')
    console.error(error)
  }
}

const handleRefreshPreview = async () => {
  if (!props.recordId && !props.printDataFetch) {
    ElMessage.warning('无打印记录')
    return
  }
  previewRefreshing.value = true
  try {
    await handleTemplateChange(selectedTemplateId.value)
  } finally {
    previewRefreshing.value = false
  }
}

const handleExportStatementExcel = async () => {
  if (!printData.value) {
    ElMessage.warning('请先加载打印预览')
    return
  }
  exportExcelLoading.value = true
  try {
    const { exportStatementPrintLayoutWorkbook } = await import('@/utils/exportStatementPrintExcel')
    await exportStatementPrintLayoutWorkbook({
      statement: {
        statement_id: props.recordId,
        statement_no: printData.value.statement_no,
        statement_name: printData.value.statement_name
      },
      printPayload: printData.value,
      templateConfig: templateConfig.value
    })
    ElMessage.success('已导出打印版 Excel')
  } catch (e) {
    ElMessage.error(e?.message || '导出 Excel 失败')
  } finally {
    exportExcelLoading.value = false
  }
}

const handleExportPdf = async () => {
  if (!previewContentRef.value || !printData.value) {
    ElMessage.warning('请先加载打印预览')
    return
  }
  exportPdfLoading.value = true
  try {
    await exportPrintPreviewPdf({
      previewContentEl: previewContentRef.value,
      templateConfig: templateConfig.value,
      printData: printData.value,
      templateType: props.templateType,
      isMultiPageStatementPreview: shouldUsePurePreviewPaged.value
    })
    ElMessage.success('已导出 PDF')
  } catch (e) {
    ElMessage.error(e?.message || '导出 PDF 失败')
  } finally {
    exportPdfLoading.value = false
  }
}

// 替换变量（页码占位符由 processHeaderFooter / applyHeaderFooterPagePlaceholders 单独处理）
const PRINT_RESERVED_PLACEHOLDERS = new Set([
  'page_number',
  'total_pages',
  'current_date',
  'current_time'
])

const replaceVariables = (text) => {
  if (!printData.value || !text) return text
  
  let result = text
  // 替换常见的变量
  Object.keys(printData.value).forEach(key => {
    if (PRINT_RESERVED_PLACEHOLDERS.has(key)) return
    if (typeof printData.value[key] === 'string' || typeof printData.value[key] === 'number') {
      result = result.replace(new RegExp(`\\{${key}\\}`, 'g'), printData.value[key])
    }
  })
  const one = getSingleItemContext()
  if (one) {
    Object.keys(one).forEach((key) => {
      if (PRINT_RESERVED_PLACEHOLDERS.has(key)) return
      if (typeof one[key] === 'string' || typeof one[key] === 'number') {
        result = result.replace(new RegExp(`\\{${key}\\}`, 'g'), one[key])
      }
    })
  }
  
  // 替换当前时间
  result = result.replace(/\{current_date\}/g, new Date().toLocaleDateString('zh-CN'))
  result = result.replace(/\{current_time\}/g, new Date().toLocaleTimeString('zh-CN'))
  
  return result
}

const renderHeaderFooterContent = (content) =>
  applyHeaderFooterPagePlaceholders(replaceVariables(content || ''), 1, 1)
const renderZoneLineContent = (content) =>
  preserveZoneLineWhitespaceHtml(stripZoneLineInlineTypography(renderHeaderFooterContent(content)))
const renderHeaderFooterContentByPage = (content, pageNo, totalPages) =>
  applyHeaderFooterPagePlaceholders(replaceVariables(content || ''), pageNo, totalPages)
const renderFooterLineContent = (content, pageNo = 1, totalPages = null, options = {}) => {
  const total = totalPages ?? Math.max(1, estimatedPages.value)
  let html = renderHeaderFooterContentByPage(content, pageNo, total)
  const useFlowCounters =
    options.useFlowCounters ??
    (useRepeatFooterEachPage.value && !shouldShowPerPagePreviewFooter.value)
  if (useFlowCounters) {
    html = injectPrintFlowPageCounters(html)
  }
  return html
}

const renderFooterZoneLineContent = (content, pageNo = 1, totalPages = null, options = {}) =>
  stripZoneLineInlineTypography(renderFooterLineContent(content, pageNo, totalPages, options))

const renderClosingZoneLineContent = (content, pageNo = 1, totalPages = null) =>
  stripZoneLineInlineTypography(
    renderHeaderFooterContentByPage(content, pageNo, totalPages ?? Math.max(1, estimatedPages.value))
  )

const renderPagedFooterHtml = (pageNo, totalPages) => {
  const cfg = templateConfig.value
  const page = Math.max(1, Math.floor(Number(pageNo)) || 1)
  const total = Math.max(1, Math.floor(Number(totalPages)) || 1)
  if (shouldRenderFooterLines(cfg)) {
    return (cfg.footerLines || [])
      .map(
        (line) =>
          `<div class="print-footer-line print-zone-line">${renderFooterZoneLineContent(line.content, page, total, {
            useFlowCounters: false
          })}</div>`
      )
      .join('')
  }
  return renderFooterLineContent(cfg.footer, page, total, { useFlowCounters: false })
}

const shouldRenderTailInPagedTable = (chunkIdx) => {
  return isPagedStatementLayout.value && chunkIdx === (printTableItemChunks.value.length - 1)
}

const resolvePaymentFooterField = (fieldKey) => {
  const pf = templateConfig.value?.paymentFooter || {}
  if (pf[fieldKey]) return pf[fieldKey]
  const selected = (pf.accounts || []).find((x) => x.key === pf.selectedAccountKey)
  if (!selected) return ''
  return selected[fieldKey] || ''
}

const paymentFooterDisplayLines = computed(() => {
  const pf = templateConfig.value?.paymentFooter
  if (!pf?.enabled) return []
  return buildPaymentFooterDisplayLines(pf, (key) => resolvePaymentFooterField(key))
})

const getSingleItemContext = () => {
  const arr = printData.value?.items
  if (!Array.isArray(arr) || arr.length !== 1) return null
  return arr[0] || null
}

const joinQuantityUnit = (qtyRaw, unitRaw) => {
  if (qtyRaw === null || qtyRaw === undefined || qtyRaw === '') return ''
  const qtyText = formatNumber(qtyRaw)
  const unitText = unitRaw == null ? '' : String(unitRaw).trim()
  return unitText ? `${qtyText} ${unitText}` : qtyText
}

// 获取字段值
const getFieldValue = (field, fieldDef = null) => {
  if (!printData.value) return ''
  if (!field || String(field).trim() === '') return ''
  if (field === '__custom_text') return String(fieldDef?.customValue || '')

  const one = getSingleItemContext()
  let value = printData.value[field]
  // 分码标签等「单条」数据经常在 items[0]，模板字段继续可直接写字段名
  if ((value === null || value === undefined || value === '') && field !== 'items') {
    if (one && Object.prototype.hasOwnProperty.call(one, field)) {
      value = one[field]
    }
  }
  if (field === 'quantity' || field === 'delivery_quantity') {
    if (isDeliveryLikePrint(props.templateType)) {
      const v =
        value ??
        (field === 'delivery_quantity' ? one?.delivery_quantity : null) ??
        one?.quantity ??
        printData.value.delivery_quantity ??
        printData.value.quantity
      return formatDeliveryOrderPrintQuantity(v ?? '')
    }
    const unitVal =
      printData.value.unit ??
      printData.value.delivery_unit ??
      one?.unit ??
      one?.delivery_unit
    return joinQuantityUnit(value, unitVal)
  }
  if (value === null || value === undefined) return ''
  return String(value)
}

// 格式化数字
const formatNumber = (num) => {
  if (num === null || num === undefined) return '0'
  const n = parseFloat(num)
  return isNaN(n) ? '0' : n.toString()
}

// 处理页眉页脚内容（替换页码占位符）
// 已由 applyHeaderFooterPagePlaceholders 统一处理，见 renderHeaderFooterContentByPage

function getTableBoxPaddingAndRow() {
  const tc = templateConfig.value.tableConfig || {}
  const rowH = Number(tc.rowHeight)
  const py = Number.isFinite(Number(tc.cellPaddingY)) ? Number(tc.cellPaddingY) : 6
  const px = Number.isFinite(Number(tc.cellPaddingX)) ? Number(tc.cellPaddingX) : 6
  return { rowH, py, px }
}

function applyTableRowBox(baseStyle) {
  const tc = templateConfig.value.tableConfig || {}
  const { rowH, py, px } = getTableBoxPaddingAndRow()
  baseStyle.padding = `${py}px ${px}px`
  baseStyle.boxSizing = 'border-box'
  if (isTableRowHeightLocked(tc) && Number.isFinite(rowH) && rowH > 0) {
    baseStyle.minHeight = `${rowH}px`
    baseStyle.lineHeight = 1.28
    baseStyle.overflow = 'visible'
    baseStyle.verticalAlign = 'top'
  }
  return baseStyle
}

/** 锁定行高时同时作用在 tr 上，部分浏览器仅靠 td 高度易在打印时被内容撑开 */
function getTableRowStyle(opts = {}) {
  if (opts.isTotal) return {}
  const tc = templateConfig.value.tableConfig || {}
  if (!isTableRowHeightLocked(tc)) return {}
  const rowH = Number(tc.rowHeight)
  if (!Number.isFinite(rowH) || rowH <= 0) return {}
  return {
    minHeight: `${rowH}px`
  }
}

// 旧版默认列回退时与模板表格行高/内边距一致；8 列均分 100% 宽
const getLegacyCellBoxStyle = (widthPct) => {
  const o = { border: tableBorderCss.value }
  if (widthPct != null) o.width = `${widthPct}%`
  Object.assign(o, { ...tableCellTextWrapStyle })
  return applyTableRowBox(o)
}

const getLegacyThStyle = (align) => {
  const w = 100 / 8
  const page = templateConfig.value.page || defaultPrintPage()
  return {
    textAlign: align || 'center',
    ...printTableHeaderCellStyle,
    fontWeight: 'bold',
    fontSize: `${deliveryDotMatrixDefaultFontPx.value}px`,
    fontFamily: resolveLegacyTableFontFamily(page, props.templateType),
    ...getLegacyCellBoxStyle(w)
  }
}

const getLegacyTdStyle = (align) => {
  const w = 100 / 8
  const page = templateConfig.value.page || defaultPrintPage()
  return {
    textAlign: align || 'left',
    fontSize: `${deliveryDotMatrixDefaultFontPx.value}px`,
    fontFamily: resolveLegacyTableFontFamily(page, props.templateType),
    ...getLegacyCellBoxStyle(w)
  }
}

function applyTableColumnLayout(baseStyle, colIndex, colspan = 1) {
  const cols = getActivePrintColumns()
  const pcts = getNormalizedTableColumnWidthPercents(cols)
  if (colspan <= 1 && pcts.length && pcts[colIndex] != null) {
    baseStyle.width = `${pcts[colIndex]}%`
  } else if (colspan > 1) {
    delete baseStyle.width
  }
  Object.assign(baseStyle, { ...tableCellTextWrapStyle })
  return baseStyle
}

// 获取表头样式
const getTableHeaderStyle = (col, colIndex, colspan = 1) => {
  const borderStyle = templateConfig.value.tableConfig?.borderStyle || 'all'
  const page = templateConfig.value.page || defaultPrintPage()
  const baseStyle = {
    ...printTableHeaderCellStyle,
    textAlign: effectiveTableHeaderTextAlign(col),
    fontWeight: 'bold',
    fontSize: resolveTablePrintFontSizePx(col.fontSize),
    fontFamily: resolveTableCellFontFamily(col, page, props.templateType)
  }
  applyTableColumnLayout(baseStyle, colIndex, colspan)
  applyTableRowBox(baseStyle)
  const mergedStyle = mergedTableHeaderPresentationStyle(colspan)
  if (mergedStyle) Object.assign(baseStyle, mergedStyle)
  if (borderStyle === 'all' || borderStyle === 'outer') {
    baseStyle.border = tableBorderCss.value
  }
  return baseStyle
}

// 获取表格单元格样式
const getTableCellStyle = (col, colIndex) => {
  const borderStyle = templateConfig.value.tableConfig?.borderStyle || 'all'
  const page = templateConfig.value.page || defaultPrintPage()
  const baseStyle = {
    textAlign: col.align || 'left',
    fontSize: resolveTablePrintFontSizePx(col.fontSize),
    fontFamily: resolveTableCellFontFamily(col, page, props.templateType)
  }
  applyTableColumnLayout(baseStyle, colIndex)
  applyTableRowBox(baseStyle)
  if (borderStyle === 'all') {
    baseStyle.border = tableBorderCss.value
  } else if (borderStyle === 'outer') {
    baseStyle.border = tableBorderCss.value
  }
  return baseStyle
}

const getTotalRowCellStyle = (col, colIndex) => {
  return {
    ...getTableCellStyle(col, colIndex),
    fontWeight: 'bold'
  }
}

const getTotalRowCellStyleForLegacy = (cidx) => {
  const col = PRINT_PREVIEW_LEGACY_COLUMNS[cidx]
  if (!col) return {}
  const align = col.align === 'right' ? 'right' : col.align === 'center' ? 'center' : 'left'
  return {
    ...getLegacyTdStyle(align),
    fontWeight: 'bold'
  }
}

// 获取表格单元格值
const getTableCellValue = (item, col, index) => {
  const field = col.field
  if (field == null || String(field).trim() === '') return ''
  if (shouldBlankMoneyCellForPrint(field, templateConfig.value.tableConfig)) return ''
  if (props.templateType === 'payment_request') {
    // 兼容旧账单模板：把出库单字段映射到账单字段，避免出现“仅金额有值”
    if (field === 'delivery_order_no') {
      // 送货单号必须对应出库单号，禁止回退到订单号
      return item.delivery_order_no || '-'
    }
    if (field === 'order_no' || field === 'client_purchase_order_no') {
      return item[field] || item.doc_no || '-'
    }
    if (field === 'delivery_date') {
      return item.delivery_date || item.biz_date || '-'
    }
    if (field === 'line_status') {
      if (item.line_status === 'pre_shipment') return '预请款'
      if (item.line_status === 'delivered') return '已出库'
      return '-'
    }
    if (field === 'line_status_reason') {
      return item.line_status_reason || '-'
    }
    if (field === 'product_name' || field === 'material_summary') {
      const v = resolveStatementLineMaterialName(item)
      if (field === 'product_name') {
        return v || item.client_material_name || item.summary || item.material_summary || '-'
      }
      return v || item.material_summary || item.summary || '-'
    }
    if (field === 'client_material_name') {
      return item.client_material_name || resolveStatementLineMaterialName(item) || '-'
    }
    if (field === 'specification' || field === 'color') {
      return item[field] || '-'
    }
    if (field === 'color_en') {
      return resolveStatementLineClientColorEn(item) || '-'
    }
    if (field === 'color_cn') {
      return resolveStatementLineClientColorCn(item) || '-'
    }
    if (field === 'quantity') {
      const qtyVal = item.quantity
      if (qtyVal === null || qtyVal === undefined || qtyVal === '') return '-'
      return formatNumber(qtyVal)
    }
    if (field === 'unit') {
      return item.unit || '-'
    }
    if (field === 'notes') {
      return item.notes || item.summary || '-'
    }
    if (field === 'dye_fee') {
      const raw = item.dye_fee ?? item.dyeFee
      if (raw === null || raw === undefined || raw === '' || Number(raw) === 0) {
        return ''
      }
      return String(Math.round(Number(raw)))
    }
  }
  if (props.templateType === 'packing_list') {
    if (field === 'color' || field === 'internal_color') {
      const en = (item.color_en != null ? String(item.color_en).trim() : '') || ''
      const cn = (item.color_cn != null ? String(item.color_cn).trim() : '') || ''
      return en || cn || (item.color != null ? String(item.color).trim() : '') || '-'
    }
    if (field === 'color_en' || field === 'internal_color_en') {
      return (item.color_en != null ? String(item.color_en).trim() : '') || '-'
    }
    if (field === 'color_cn' || field === 'internal_color_cn') {
      return (item.color_cn != null ? String(item.color_cn).trim() : '') || '-'
    }
    if (field === 'product_name' || field === 'material_name') {
      const v = (item.product_name != null ? String(item.product_name).trim() : '')
        || (item.material_name != null ? String(item.material_name).trim() : '')
      return v || '-'
    }
    if (field === 'quantity' || field === 'pack_quantity') {
      const q = item.pack_quantity ?? item.quantity
      return q === null || q === undefined || q === '' ? '-' : formatNumber(q)
    }
    if (field === 'gw_kg' || field === 'nw_kg' || field === 'cbm') {
      const v = item[field]
      if (v === null || v === undefined || v === '') return '-'
      return String(v)
    }
    if (field === 'package_size' || field === 'ctn_size') {
      return (item.package_size || item.ctn_size || '').trim() || '-'
    }
  }
  if (isDeliveryLikePrint(props.templateType)) {
    if (field === 'product_name') {
      const v = (item.product_name != null && String(item.product_name).trim()) ? String(item.product_name).trim() : ''
      return v || '-'
    }
    if (field === 'material_summary') {
      const ms = item.material_summary != null ? String(item.material_summary).trim() : ''
      if (ms) return ms
      const n = (item.product_name != null ? String(item.product_name).trim() : '') || ''
      const ce = (item.color_en != null ? String(item.color_en).trim() : '') || ''
      const cc = (item.color_cn != null ? String(item.color_cn).trim() : '') || ''
      const colorPart =
        ce && cc && ce !== cc ? `${ce} / ${cc}` : (ce || cc || (item.color != null ? String(item.color).trim() : '') || '')
      if (n && colorPart) return `${n} / ${colorPart}`
      return n || colorPart || '-'
    }
  }
  if (field === 'material_summary') {
    const parts = [
      item.material_summary,
      item.product_name,
      item.color_en,
      item.color_cn
    ].filter((x) => x !== undefined && x !== null && String(x).trim() !== '')
    return parts.length ? String(parts[0] === item.material_summary ? item.material_summary : `${item.product_name || ''}${item.color_en || item.color_cn ? ' / ' : ''}${[item.color_en, item.color_cn].filter(Boolean).join(' / ')}`) : '-'
  }
  if (col.field === 'serial') {
    return item.serial || index + 1
  } else if (col.field === 'color') {
    if (item.color_en && item.color_cn) {
      return `${item.color_en} / ${item.color_cn}`
    } else if (item.color_en) {
      return item.color_en
    } else if (item.color_cn) {
      return item.color_cn
    }
    return item.color || '-'
  } else if (col.field === 'quantity') {
    if (props.templateType === 'payment_request') {
      const qtyVal = item[col.field]
      return qtyVal === null || qtyVal === undefined || qtyVal === '' ? '-' : formatNumber(qtyVal)
    }
    if (isDeliveryLikePrint(props.templateType)) {
      const q = item[col.field] ?? item.delivery_quantity
      return formatDeliveryOrderPrintQuantity(q ?? '')
    }
    return joinQuantityUnit(item[col.field] || item.delivery_quantity || 0, item.unit || item.delivery_unit)
  } else if (col.field === 'unit_price' || col.field === 'line_amount' || col.field === 'amount' || col.field === 'item_amount') {
    const v = (() => {
      if (field === 'unit_price') return item.unit_price ?? item.unitPrice
      if (field === 'line_amount') return item.line_amount ?? item.item_amount ?? item.amount
      if (field === 'item_amount') return item.item_amount ?? item.line_amount ?? item.amount
      if (field === 'amount') return item.amount ?? item.line_amount ?? item.item_amount
      return item[field]
    })()
    if (v === undefined || v === null || v === '') return '-'
    return Number(v).toFixed(2)
  } else if (col.field === 'dye_fee' && props.templateType !== 'payment_request') {
    const v = item.dye_fee ?? item.dyeFee ?? 0
    if (v === undefined || v === null || v === '') return '-'
    return Number(v).toFixed(2)
  }
  const raw = item[col.field]
  if (raw === undefined || raw === null || raw === '') return '-'
  return raw
}


// 获取基本信息字段样式（新格式）
const getBasicFieldStyle = (field) => {
  const page = templateConfig.value.page || defaultPrintPage()
  return {
    fontSize: resolveTablePrintFontSizePx(field.fontSize),
    fontFamily: resolveBasicFieldFontFamily(field, page, props.templateType),
    fontWeight: field.fontWeight || 'normal',
    textAlign: field.align || 'left',
    marginBottom: '0'
  }
}

// 获取字段样式（旧格式兼容）
const getFieldStyle = (field) => {
  const page = templateConfig.value.page || defaultPrintPage()
  const ff = resolveBasicFieldFontFamily(field, page, props.templateType)
  // 如果字段有坐标信息，使用绝对定位；否则使用普通布局
  if (field.x !== undefined && field.y !== undefined) {
    return {
      position: 'absolute',
      left: `${field.x}px`,
      top: `${field.y}px`,
      width: field.width ? `${field.width}px` : 'auto',
      height: field.height ? `${field.height}px` : 'auto',
      fontSize: resolveTablePrintFontSizePx(field.fontSize),
      fontFamily: ff,
      textAlign: field.align || 'left',
      lineHeight: field.height ? `${field.height}px` : 'normal',
      overflow: 'hidden'
    }
  } else {
    return {
      fontSize: resolveTablePrintFontSizePx(field.fontSize),
      fontFamily: ff,
      textAlign: field.align || 'left',
      marginBottom: '5px'
    }
  }
}

const signatureFieldValue = (item) =>
  resolveSignatureLineValue(item, (k) => getFieldValue(k) || '')

const dualGridCellValue = (cell) =>
  resolveStatementDualGridCellValue(cell, (k) => getFieldValue(k) || '')

// 获取表格样式
const getTableStyle = () => {
  const borderStyle = templateConfig.value.tableConfig?.borderStyle || 'all'
  const baseStyle = {
    width: '100%',
    tableLayout: 'fixed',
    borderCollapse: 'collapse'
  }
  
  if (borderStyle === 'all' || borderStyle === 'outer') {
    baseStyle.border = tableBorderCss.value
  } else {
    baseStyle.border = 'none'
  }
  
  return baseStyle
}

// 打印
const handlePrint = () => {
  if (!previewContentRef.value) return

  const pageCfg = templateConfig.value.page || defaultPrintPage()
  const printWindow = window.open('about:blank', '_blank')
  if (!printWindow) {
    ElMessage.error('无法打开打印窗口，请在浏览器中允许本站弹出窗口')
    return
  }

  if (shouldUsePurePreviewPaged.value) {
    const printContent = clonePurePrintPagesHtml(previewContentRef.value, {
      rowsPerPage: effectiveRowsPerPage.value
    })
    if (!printContent.trim()) {
      printWindow.close()
      ElMessage.warning('暂无打印内容')
      return
    }
    const paperMm = getPhysicalPaperMm(pageCfg)
    const footerLayout = printFooterLayout.value
    const printBodyFf = effectiveBodyFontFamily(pageCfg, props.templateType)
    const printBodyFs = isPhysicalPrinterTemplate.value ? 16 : 12
    printWindow.document.write(
      buildPureDomPrintDocumentHtml({
        bodyHtml: printContent,
        atPageSize: buildAtPageSizeCss(pageCfg),
        paperWmm: paperMm.w,
        paperHmm: paperMm.h,
        marginTop: Number(pageCfg.marginTop ?? 10) || 10,
        marginRight: Number(pageCfg.marginRight ?? 10) || 10,
        marginBottom: Number(pageCfg.marginBottom ?? 10) || 10,
        marginLeft: Number(pageCfg.marginLeft ?? 10) || 10,
        footerHeightMm: footerLayout.heightMm,
        tableBorderCss: tableBorderCss.value,
        tableBorderWidthPx: tableBorderWidthPx.value,
        printBodyFf,
        printBodyFs,
        title: '打印'
      })
    )
    printWindow.document.close()
    printWindow.focus()
    return
  }

  const hasFooter = hasTemplateFooter.value
  const footerLayout = resolvePrintFooterLayout(pageCfg)
  const repeatFooter = useRepeatFooterEachPage.value
  const pageMarginCss = buildPageMarginCssString(pageCfg, {
    reserveFooter: repeatFooter && hasFooter
  })
  const footerAlign = normalizeFooterAlign(templateConfig.value.footerAlign)
  const paperMm = getPhysicalPaperMm(pageCfg)
  const innerHeightMm = Math.max(
    1,
    Number(paperMm.h) - Number(pageCfg.marginTop ?? 10) - Number(pageCfg.marginBottom ?? 10)
  )
  const printContent = cloneStandardPrintContentHtml(previewContentRef.value)
  const hmt = Math.max(0, Number(pageCfg.headerMarginTopPx ?? 0) || 0)
  const hmb = Math.max(0, Number(pageCfg.headerMarginBottomPx ?? 0) || 0)
  const hPad = Math.max(0, Number(pageCfg.headerPaddingPx ?? 4) || 0)
  const bfMy = Math.max(0, Number(pageCfg.basicFieldsSectionMarginYpx ?? 2) || 0)
  const bfRowGap = Math.max(0, Number(pageCfg.basicFieldsRowGapPx ?? 2) || 0)
  const bfLh = clampPrintBlockLineHeight(pageCfg.basicFieldsLineHeight, 1.22)
  const sigLh = clampPrintBlockLineHeight(pageCfg.signaturesLineHeight, 1.22)
  const ttm = Math.max(0, Number(pageCfg.tableSectionMarginTopPx ?? 0) || 0)
  const signsMt = Math.max(0, Number(pageCfg.signaturesMarginTopPx ?? 10) || 0)
  const {
    lhCss: headerLineHeightCss,
    fs: headerFs,
    gapEm: headerGapEm,
    hmEm: headerHmEm,
    imgPx: headerImgPx,
    zoom: headerZoom
  } = clampHeaderLayoutFromPage(pageCfg)
  const headerZoomCss = headerZoom < 0.999 ? `zoom: ${headerZoom} !important;` : ''

  const printBodyFf = effectiveBodyFontFamily(pageCfg, props.templateType)
  const printHeaderFf = effectiveHeaderFontFamily(pageCfg, props.templateType)
  const printBodyFs = isPhysicalPrinterTemplate.value ? 16 : 12
  const printBodyColor = '#000'
  const physicalPrintCss = buildPhysicalPrinterEnhancementCss({
    printBodyFs,
    printBodyFf,
    isDeliveryLike: isPhysicalPrinterTemplate.value
  })
  const fixedFooterCss = footerLayout.fixed
    ? buildFixedPrintFooterCss({ heightMm: footerLayout.heightMm, bodyFontFamily: printBodyFf })
    : ''
  const repeatFooterCss =
    repeatFooter && hasFooter
      ? buildRepeatEachPagePrintFooterCss({
          heightMm: footerLayout.heightMm,
          bodyFontFamily: printBodyFf,
          footerAlign,
          marginBottomMm: Number(pageCfg.marginBottom ?? 10) || 10
        })
      : ''
  const pageCounterCss = repeatFooter && hasFooter ? buildPrintPageCounterCss() : ''
  const pureFooterHeightCss = footerLayout.heightCss

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>打印预览</title>
      <style>
        ${PRINT_PREVIEW_TOOLBAR_CSS}
        @media print {
          @page { size: ${buildAtPageSizeCss(templateConfig.value.page)}; margin: ${pageMarginCss}; }
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            height: auto !important;
            min-height: 0 !important;
          }
          /* 预览把页边距做在 .print-page 的 padding 上；打印时再叠 @page margin 会双重留白，此处归零仅用 @page */
          .print-page {
            width: 100% !important;
            max-width: none !important;
            min-height: 0 !important;
            height: auto !important;
            margin: 0 auto !important;
            padding: 0 !important;
            box-shadow: none !important;
            background: #fff !important;
          }
          /* 「缩至单页」预览用的 scale 进入打印会整体变小，打印时必须清除 */
          .print-page__content-shell {
            overflow: visible !important;
            width: 100% !important;
            height: auto !important;
            max-height: none !important;
          }
          .print-page__content {
            transform: none !important;
            width: 100% !important;
            height: auto !important;
            max-height: none !important;
            will-change: auto !important;
          }
          .print-table table {
            width: 100% !important;
            table-layout: fixed !important;
            border-collapse: collapse !important;
          }
          .print-table th,
          .print-table td {
            box-sizing: border-box !important;
          }
          .print-table thead th {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print-header {
            zoom: 1 !important;
            transform: none !important;
          }
          .print-table th,
          .print-table td {
            overflow: visible !important;
            height: auto !important;
            max-height: none !important;
            line-height: 1.35 !important;
          }
          .print-table tbody tr {
            height: auto !important;
            max-height: none !important;
          }
        }
        ${physicalPrintCss}
        body { font-size: ${printBodyFs}px; color: ${printBodyColor}; font-family: ${printBodyFf}; }
        .print-page {
          box-sizing: border-box !important;
          min-height: ${footerLayout.fixed ? `${innerHeightMm}mm` : '0'} !important;
          height: auto !important;
          max-height: none !important;
          display: ${footerLayout.fixed ? 'flex' : 'table'};
          flex-direction: ${footerLayout.fixed ? 'column' : 'unset'};
          table-layout: fixed;
          width: 100%;
          box-shadow: none !important;
          margin: 0 !important;
        }
        .print-body { display: ${footerLayout.fixed ? 'block' : 'table-row'}; height: auto; font-family: ${printBodyFf}; }
        .print-footer { display: ${footerLayout.fixed ? 'flex' : 'table-row'}; height: ${footerLayout.fixed ? pureFooterHeightCss : '8mm'}; vertical-align: bottom; font-family: ${printBodyFf}; }
        ${fixedFooterCss}
        ${repeatFooterCss}
        ${pageCounterCss}
        .preview-table-page--with-footer {
          page-break-after: always;
          break-after: page;
          min-height: ${innerHeightMm}mm !important;
          display: flex !important;
          flex-direction: column !important;
          box-sizing: border-box !important;
        }
        .preview-table-page--with-footer:last-of-type {
          page-break-after: auto;
          break-after: auto;
        }
        .print-page-footer-slot {
          flex: 0 0 ${pureFooterHeightCss} !important;
          height: ${pureFooterHeightCss} !important;
          min-height: ${pureFooterHeightCss} !important;
          max-height: ${pureFooterHeightCss} !important;
          margin-top: auto !important;
          overflow: hidden !important;
          box-sizing: border-box !important;
        }
        .print-table thead th { background: #fff !important; box-shadow: none !important; }
        .print-table thead th.print-table-header--merged {
          text-align: center !important;
          vertical-align: middle !important;
        }
        .print-header {
          padding: ${hPad}px 10px !important;
          border-bottom: 1px solid #eee;
          margin-top: ${hmt}px !important;
          margin-bottom: ${hmb}px !important;
          line-height: ${headerLineHeightCss} !important;
          font-size: ${headerFs}px !important;
          font-family: ${printHeaderFf};
          ${headerZoomCss}
        }
        .print-header p { margin: ${headerGapEm}em 0 !important; }
        .print-header h1, .print-header h2, .print-header h3 {
          margin: ${headerHmEm}em 0 !important;
          line-height: 1.12 !important;
        }
        .print-header img {
          max-width: 100% !important;
          height: auto !important;
          max-height: ${headerImgPx}px !important;
          vertical-align: top !important;
        }
        .basic-fields-section {
          margin: ${bfMy}px 0 !important;
          --bf-row-gap: ${bfRowGap}px;
          line-height: ${bfLh} !important;
          font-family: ${printBodyFf};
        }
        .print-table-region { margin-top: ${ttm}px !important; }
        .pure-print-table-region { margin-top: ${ttm}px !important; }
        .print-signatures--spread {
          display: flex !important;
          flex-direction: row !important;
          flex-wrap: nowrap !important;
          justify-content: space-between !important;
          align-items: flex-end !important;
          width: 100% !important;
          box-sizing: border-box !important;
          margin-top: ${signsMt}px !important;
          gap: 8px !important;
          line-height: ${sigLh} !important;
          font-family: ${printBodyFf};
        }
        .print-signature-slot {
          flex: 1 1 0 !important;
          min-width: 0 !important;
          max-width: none !important;
          text-align: center;
          box-sizing: border-box;
        }
        .print-signature-slot__label {
          font-size: ${printBodyFs}px;
          margin-bottom: 2px;
          line-height: inherit;
        }
        
.print-payment-footer__table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  margin-top: 4px;
  font-size: inherit;
}
.print-payment-footer__table td {
  border: 1px solid #000;
  padding: 4px 8px;
  vertical-align: top;
}
.print-payment-footer__table .pf-label {
  white-space: nowrap;
}
.print-payment-footer__table .pf-value {
  word-break: break-word;
}
.print-payment-footer--bordered .print-payment-footer__title {
  margin-bottom: 4px;
}
.print-signatures--dual-grid {
  display: block;
  width: 100%;
}
.print-signature-dual-grid {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}
.print-signature-dual-grid td {
  border: none;
  padding: 6px 8px 2px;
  vertical-align: bottom;
  width: 50%;
}
.print-signature-dual-grid .sig-cell {
  vertical-align: top;
}
.print-signature-dual-grid .sig-lbl {
  display: block;
  white-space: normal;
  margin-right: 0;
  margin-bottom: 2px;
}
.print-signature-dual-grid .sig-val {
  display: block;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  word-break: break-word;
  border-bottom: 1px solid #000;
  min-height: 1.35em;
}

.print-signature-slot__line {
          border-bottom: 1px solid #000;
          min-height: 1.45em;
          line-height: inherit;
          font-size: ${printBodyFs}px;
        }
        .basic-fields-split-row { display: flex; justify-content: space-between; align-items: flex-start; width: 100%; gap: 8px; margin-bottom: var(--bf-row-gap, 2px) !important; }
        .basic-fields-split-left { flex: 1; min-width: 0; }
        .basic-fields-split-right { flex-shrink: 0; display: flex; flex-direction: column; align-items: flex-end; }
        .basic-fields-grid-row { display: flex; flex-wrap: nowrap; gap: 6px; width: 100%; margin-bottom: var(--bf-row-gap, 2px) !important; }
        .basic-fields-grid-cell { min-width: 0; }
        .basic-fields-label { margin-right: 5px; font-weight: bold; }
      </style>
    </head>
    <body>
    ${buildPrintPreviewToolbarHtml()}
    ${buildPrintWindowHintHtml()}
    <div class="erp-print-doc">${printContent}</div>
    </body>
    </html>
  `)

  printWindow.document.close()
  printWindow.focus()
}

// 关闭
const handleClose = () => {
  fitModeOverride.value = '__template__'
  rowsPerPageOverride.value = 0
  emit('update:modelValue', false)
}

// 对话框打开且已有 recordId 后再拉模板/数据，避免与 v-model 同一帧更新时 recordId 未就绪
watch(
  () => [props.modelValue, props.recordId],
  async ([open, rid]) => {
    if (!open) {
      fitModeOverride.value = '__template__'
      rowsPerPageOverride.value = 0
      return
    }
    await nextTick()
    if (rid == null || rid === '') {
      return
    }
    await loadTemplates()
  },
  { immediate: true }
)

// 派工明细 / 分码筛选变化时刷新预览（例如移动端切换「第 n 条」时对话框仍打开）
watch(
  () => [props.dispatchOrderItemId, props.branchRecordId],
  async () => {
    if (!props.modelValue || !selectedTemplateId.value) return
    await handleTemplateChange(selectedTemplateId.value)
  }
)
</script>

<style scoped>
.print-preview-container {
  display: flex;
  flex-direction: column;
  height: 70vh;
}

.preview-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 0;
  border-bottom: 1px solid #dcdfe6;
  margin-bottom: 20px;
}

.toolbar-inline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.toolbar-inline__label {
  font-size: 12px;
  color: #374151;
}

.toolbar-inline__hint {
  font-size: 12px;
  color: #6b7280;
}

.preview-content {
  flex: 1;
  overflow: auto;
  background: #f5f5f5;
  padding: 20px;
  display: flex;
  justify-content: center;
}

.preview-template-meta {
  margin: 8px 0 0;
  font-size: 12px;
  color: #6b7280;
}

.pure-preview-pages {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.pure-print-page {
  border: 1px dashed #d1d5db;
  border-radius: 4px;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
}

.pure-print-page:last-child {
  margin-bottom: 0 !important;
}

.pure-print-main {
  overflow: hidden;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.pure-print-footer {
  border-top: 1px solid #eee;
  overflow: visible;
  line-height: 1.2;
  white-space: normal;
  word-break: break-word;
}

.pure-print-footer :deep(.print-footer-line) {
  width: 100%;
  white-space: normal !important;
  word-break: break-word !important;
  overflow: visible !important;
  line-height: 1.2 !important;
}

.pure-print-footer :deep(.print-footer-line p),
.pure-print-footer :deep(.print-footer-line div),
.pure-print-footer :deep(.print-footer-line span),
.pure-print-footer :deep(.page-number),
.pure-print-footer :deep(.total-pages) {
  display: inline !important;
  white-space: inherit !important;
  margin: 0 !important;
}

.print-page--paged-preview {
  background: transparent !important;
  box-shadow: none !important;
}

.print-page--paged-preview .print-table-region {
  margin-top: 0;
}

.pure-print-page .print-table {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  padding-bottom: var(--print-table-border-width, 0.75px);
  box-sizing: border-box;
}

.pure-preview-pages :deep(.pure-print-table) {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.pure-preview-pages :deep(.pure-print-table th),
.pure-preview-pages :deep(.pure-print-table td) {
  border: var(--print-table-border, 0.75px solid #000);
  box-sizing: border-box;
}

.pure-preview-pages :deep(.pure-print-table th) {
  background: #fff !important;
  background-color: #fff !important;
  box-shadow: none !important;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.pure-preview-pages :deep(.pure-print-notes) {
  margin-top: 6px;
  white-space: pre-wrap;
}

.pure-preview-pages :deep(.pure-print-payment) {
  margin-top: 6px;
  line-height: 1.55;
}

.pure-preview-pages :deep(.pure-print-payment .t) {
  font-weight: 600;
  margin-bottom: 4px;
}

.pure-preview-pages :deep(.pure-print-signs) {
  margin-top: 8px;
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  justify-content: space-between;
  align-items: flex-end;
}

.pure-preview-pages :deep(.pure-print-signs .s) {
  flex: 1 1 0;
  min-width: 0;
  text-align: center;
}

.pure-preview-pages :deep(.pure-print-signs .l) {
  margin-bottom: 4px;
}

.pure-preview-pages :deep(.pure-print-signs .v) {
  border-bottom: 1px solid #000;
  min-height: 28px;
  line-height: 28px;
}

.print-page {
  position: relative;
  background: white;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
}

.print-page--fixed-footer .print-page__content-shell,
.print-page--fixed-footer .print-page__content {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  box-sizing: border-box;
}

.print-page--fixed-footer .print-body {
  flex: 1 1 auto;
  min-height: 0;
}

.print-page--fixed-footer .print-footer {
  flex: 0 0 var(--print-footer-height, 7mm);
  height: var(--print-footer-height, 7mm);
  min-height: var(--print-footer-height, 7mm);
  max-height: var(--print-footer-height, 7mm);
  margin-top: auto;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  box-sizing: border-box;
  padding: 0 10px;
}

.print-header {
  border-bottom: 1px solid #eee;
}

.print-header :deep(p) {
  margin: var(--header-p-gap, 0.08em) 0;
}

.print-header :deep(h1),
.print-header :deep(h2),
.print-header :deep(h3) {
  margin: var(--header-h-margin, 0.12em) 0;
  line-height: 1.12;
}

.print-header :deep(img) {
  max-width: 100%;
  height: auto !important;
  max-height: var(--header-img-max, 48px);
  vertical-align: top;
}

.print-header-line :deep(p),
.print-footer-line :deep(p) {
  margin: 0.06em 0;
}

.print-header-line :deep(h1),
.print-header-line :deep(h2),
.print-header-line :deep(h3),
.print-footer-line :deep(h1),
.print-footer-line :deep(h2),
.print-footer-line :deep(h3) {
  margin: 0.08em 0;
  line-height: 1.12;
}

.print-header-line :deep(img),
.print-footer-line :deep(img) {
  max-width: 100%;
  height: auto !important;
  max-height: var(--header-img-max, 48px);
  vertical-align: top;
}

.print-zone-line :deep(p),
.print-zone-line :deep(span),
.print-zone-line :deep(div),
.print-zone-line :deep(strong),
.print-zone-line :deep(em) {
  font-size: inherit !important;
  font-family: inherit !important;
  font-weight: inherit !important;
  line-height: inherit !important;
  text-align: inherit;
  color: inherit;
}

.print-zone-line :deep(p) {
  margin: 0;
  padding: 0;
  white-space: pre-wrap;
}

.print-body {
  position: relative;
  min-height: 0;
  flex: 0 0 auto;
}

.print-page--label .print-body {
  min-height: 0;
}

.print-page--label .basic-fields-section {
  margin: 0;
}

.print-field {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.print-page__content-shell,
.print-page__content {
  width: 100%;
  box-sizing: border-box;
}

.print-page--compact-tail .print-payment-footer {
  margin-top: 8px;
  padding-top: 6px;
}

.print-page--compact-tail .print-signatures--spread {
  margin-top: 0;
}

.print-table table {
  table-layout: fixed;
  width: 100%;
}

.print-table table thead th {
  background: #fff !important;
  background-color: #fff !important;
  box-shadow: none !important;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.print-table table thead th.print-table-header--merged {
  text-align: center !important;
  vertical-align: middle !important;
}

.print-table--chunk-break {
  page-break-after: always;
  break-after: page;
}

.preview-table-page__index {
  font-size: 12px;
  color: #6b7280;
  margin: 2px 0 6px;
}

.preview-table-page__footer {
  margin-top: auto;
  padding-top: 8px;
  text-align: center;
  font-size: 12px;
  color: #374151;
  border-top: 1px solid #d1d5db;
}

.print-footer--in-paged-preview {
  padding: 0;
  border: none;
}

.print-payment-footer {
  border-top: 1px dashed #dcdfe6;
  padding-top: 6px;
  font-size: 12px;
  line-height: 1.55;
}

.print-payment-footer__title {
  font-weight: 600;
  margin-bottom: 4px;
}

.print-footer {
  padding: 10px;
  border-top: 1px solid #eee;
  flex-shrink: 0;
}

.print-page--fixed-footer .print-footer {
  padding: 0 10px;
}

.basic-fields-section > .basic-fields-split-row:last-child,
.basic-fields-section > .basic-fields-grid-row:last-child,
.basic-fields-section > .basic-fields-cell--full:last-child {
  margin-bottom: 0 !important;
}

.basic-fields-split-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  gap: 8px;
  margin-bottom: var(--bf-row-gap, 2px);
}

.basic-fields-split-left {
  flex: 1;
  min-width: 0;
}

.basic-fields-split-right {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.basic-fields-grid-row {
  display: flex;
  flex-wrap: nowrap;
  gap: 6px;
  margin-bottom: var(--bf-row-gap, 2px);
  width: 100%;
}

.basic-fields-grid-cell {
  min-width: 0;
}

.basic-fields-label {
  margin-right: 5px;
  font-weight: bold;
}

.basic-fields-cell--full {
  width: 100%;
}

.basic-fields-cell--rightCol {
  width: 100%;
}

.print-signatures--spread {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;
  margin-top: 0;
  gap: 8px;
  flex-wrap: nowrap;
}

.print-signature-slot {
  flex: 1 1 0;
  min-width: 0;
  max-width: none;
  text-align: center;
  box-sizing: border-box;
  padding: 0 4px;
}

.print-signature-slot__label {
  font-size: 12px;
  margin-bottom: 3px;
  line-height: inherit;
}


.print-payment-footer__table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  margin-top: 4px;
  font-size: inherit;
}
.print-payment-footer__table td {
  border: 1px solid #000;
  padding: 4px 8px;
  vertical-align: top;
}
.print-payment-footer__table .pf-label {
  white-space: nowrap;
}
.print-payment-footer__table .pf-value {
  word-break: break-word;
}
.print-payment-footer--bordered .print-payment-footer__title {
  margin-bottom: 4px;
}
.print-signatures--dual-grid {
  display: block;
  width: 100%;
}
.print-signature-dual-grid {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}
.print-signature-dual-grid td {
  border: none;
  padding: 6px 8px 2px;
  vertical-align: bottom;
  width: 50%;
}
.print-signature-dual-grid .sig-cell {
  vertical-align: top;
}
.print-signature-dual-grid .sig-lbl {
  display: block;
  white-space: normal;
  margin-right: 0;
  margin-bottom: 2px;
}
.print-signature-dual-grid .sig-val {
  display: block;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  word-break: break-word;
  border-bottom: 1px solid #000;
  min-height: 1.35em;
}

.print-signature-slot__line {
  border-bottom: 1px solid #000;
  min-height: 1.45em;
  line-height: inherit;
  font-size: 12px;
}
</style>

